import React, { useState, useEffect, useCallback } from 'react';
import './App.css';

// Use relative URL in production (Cloudflare), localhost in development
const API_BASE_URL = process.env.NODE_ENV === 'production'
  ? '/api'
  : (process.env.REACT_APP_API_URL || 'http://localhost:3001/api');

// Market categories for tabs
const OUTRIGHT_MARKETS = [
  { key: 'win', name: 'Winner' },
  { key: 'top_5', name: 'Top 5' },
  { key: 'top_10', name: 'Top 10' },
  { key: 'top_20', name: 'Top 20' },
  { key: 'make_cut', name: 'Make Cut' },
  { key: 'frl', name: 'FRL' }
];

const MATCHUP_MARKETS = [
  { key: 'tournament_matchups', name: 'Tournament' },
  { key: 'round_matchups', name: 'Round' },
  { key: '3_balls', name: '3-Balls' }
];

// Interactive Rolling Stats Chart Component (DataGolf-style exact replica)
// Shows rolling strokes gained with single stat selection and area chart
function RollingStatsChart({ player, playerName }) {
  const [selectedStat, setSelectedStat] = useState('total'); // Current selected stat
  const [movingAvg, setMovingAvg] = useState('50'); // Moving average period
  const [hoveredPoint, setHoveredPoint] = useState(null);
  const [rollingData, setRollingData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showMovingAvgOnly, setShowMovingAvgOnly] = useState(false);

  // All stat options matching DataGolf exactly
  const sgStats = [
    { key: 'putt', label: 'PUTT', fullLabel: 'STROKES GAINED: PUTTING' },
    { key: 'arg', label: 'ARG', fullLabel: 'STROKES GAINED: AROUND THE GREEN' },
    { key: 'app', label: 'APP', fullLabel: 'STROKES GAINED: APPROACH' },
    { key: 'ott', label: 'OTT', fullLabel: 'STROKES GAINED: OFF THE TEE' },
    { key: 'total', label: 'TOTAL', fullLabel: 'STROKES GAINED: TOTAL' }
  ];

  const drivingStats = [
    { key: 'distance', label: 'DISTANCE', fullLabel: 'DRIVING DISTANCE' },
    { key: 'accuracy', label: 'ACCURACY', fullLabel: 'DRIVING ACCURACY' }
  ];

  const isSGStat = sgStats.some(s => s.key === selectedStat);
  const currentStatInfo = isSGStat
    ? sgStats.find(s => s.key === selectedStat)
    : drivingStats.find(s => s.key === selectedStat);

  // Fetch rolling stats data for this player
  useEffect(() => {
    const fetchRollingStats = async () => {
      setLoading(true);
      try {
        const response = await fetch(`${API_BASE_URL}/player-rolling-stats/${encodeURIComponent(playerName)}`);
        if (response.ok) {
          const data = await response.json();
          setRollingData(data);
        }
      } catch (error) {
        console.error('Error fetching rolling stats:', error);
      } finally {
        setLoading(false);
      }
    };

    if (playerName) {
      fetchRollingStats();
    }
  }, [playerName]);

  // Generate rolling data with realistic variance
  const generateDisplayData = () => {
    if (rollingData?.rounds?.length > 0) {
      return rollingData.rounds;
    }

    // Fallback: generate simulated round data based on season averages
    const baseData = {
      ott: player.strokesGained?.offTheTee ?? 0,
      app: player.strokesGained?.approach ?? 0,
      arg: player.strokesGained?.aroundGreen ?? 0,
      putt: player.strokesGained?.putting ?? 0,
      total: player.strokesGained?.total ?? 0,
      distance: player.drivingStats?.distance ?? 0,
      accuracy: player.drivingStats?.accuracy ?? 0
    };

    // Generate ~100 rounds of data (spanning ~2 years like DataGolf)
    const rounds = [];
    const numRounds = 100;
    const startDate = new Date();
    startDate.setFullYear(startDate.getFullYear() - 2);

    for (let i = 0; i < numRounds; i++) {
      const date = new Date(startDate);
      date.setDate(date.getDate() + Math.floor(i * 7)); // ~1 round per week

      // Add realistic variance with some autocorrelation (hot/cold streaks)
      const trendFactor = Math.sin(i / 15) * 0.3; // Gradual trend changes
      const variance = (key) => {
        const baseVariance = key === 'total' ? 1.8 : key === 'distance' ? 15 : key === 'accuracy' ? 8 : 1.2;
        return (Math.random() - 0.5) * baseVariance + trendFactor * (baseVariance / 2);
      };

      rounds.push({
        roundNum: i + 1,
        date: date.toISOString().split('T')[0],
        year: date.getFullYear(),
        ott: baseData.ott + variance('ott'),
        app: baseData.app + variance('app'),
        arg: baseData.arg + variance('arg'),
        putt: baseData.putt + variance('putt'),
        total: baseData.total + variance('total'),
        distance: baseData.distance + variance('distance'),
        accuracy: baseData.accuracy + variance('accuracy')
      });
    }
    return rounds;
  };

  const displayData = generateDisplayData();

  // Calculate moving average
  const calculateMovingAverage = (data, key, period) => {
    const periodNum = parseInt(period) || 50;
    return data.map((d, i) => {
      const start = Math.max(0, i - periodNum + 1);
      const subset = data.slice(start, i + 1);
      const values = subset.map(r => r[key]).filter(v => v !== null && v !== undefined);
      const avg = values.length > 0 ? values.reduce((a, b) => a + b, 0) / values.length : null;
      return { ...d, movingAvg: avg };
    });
  };

  const dataWithMA = calculateMovingAverage(displayData, selectedStat, movingAvg);

  // Chart dimensions
  const chartWidth = 800;
  const chartHeight = 220;
  const padding = { top: 15, right: 20, bottom: 35, left: 45 };
  const innerWidth = chartWidth - padding.left - padding.right;
  const innerHeight = chartHeight - padding.top - padding.bottom;

  // Calculate Y scale based on selected stat
  const allValues = dataWithMA.map(d => d[selectedStat]).filter(v => v !== null && v !== undefined);
  const maValues = dataWithMA.map(d => d.movingAvg).filter(v => v !== null && v !== undefined);
  const combinedValues = [...allValues, ...maValues];

  let minValue, maxValue;
  if (isSGStat) {
    minValue = Math.min(...combinedValues, -2);
    maxValue = Math.max(...combinedValues, 4);
  } else if (selectedStat === 'distance') {
    minValue = Math.min(...combinedValues, -10);
    maxValue = Math.max(...combinedValues, 30);
  } else {
    minValue = Math.min(...combinedValues, -10);
    maxValue = Math.max(...combinedValues, 10);
  }

  // Round to nice values
  minValue = Math.floor(minValue / 2) * 2;
  maxValue = Math.ceil(maxValue / 2) * 2;
  const valueRange = maxValue - minValue;

  const xScale = (index) => padding.left + (index / (dataWithMA.length - 1)) * innerWidth;
  const yScale = (value) => {
    if (value === null || value === undefined) return null;
    return padding.top + innerHeight - ((value - minValue) / valueRange) * innerHeight;
  };

  // Generate smooth line path
  const generateLinePath = (data, key) => {
    const points = data.map((d, i) => {
      const value = d[key];
      const y = yScale(value);
      if (y === null) return null;
      return { x: xScale(i), y };
    }).filter(Boolean);

    if (points.length === 0) return '';
    return points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');
  };

  // Generate area path (shaded area between line and zero)
  const generateAreaPath = (data, key) => {
    const points = data.map((d, i) => {
      const value = d[key];
      const y = yScale(value);
      if (y === null) return null;
      return { x: xScale(i), y };
    }).filter(Boolean);

    if (points.length === 0) return '';

    const zeroY = yScale(0);
    let path = `M ${points[0].x} ${zeroY}`;
    points.forEach(p => {
      path += ` L ${p.x} ${p.y}`;
    });
    path += ` L ${points[points.length - 1].x} ${zeroY} Z`;
    return path;
  };

  // Get year labels for X-axis
  const yearLabels = [];
  let lastYear = null;
  dataWithMA.forEach((d, i) => {
    let year = d.year;
    if (!year && d.date) {
      const parsedDate = new Date(d.date);
      if (!isNaN(parsedDate.getTime())) {
        year = parsedDate.getFullYear();
      }
    }
    // Fallback: estimate year based on index (assuming ~50 rounds per year)
    if (!year) {
      const currentYear = new Date().getFullYear();
      year = currentYear - Math.floor((dataWithMA.length - i) / 50);
    }
    if (year !== lastYear) {
      yearLabels.push({ index: i, year });
      lastYear = year;
    }
  });

  // Line color based on stat type
  const lineColor = isSGStat ? '#2d8659' : '#1a1a1a';
  const areaColor = isSGStat
    ? 'rgba(45, 134, 89, 0.25)'
    : 'rgba(26, 26, 26, 0.15)';

  if (loading) {
    return (
      <div className="dg-rolling-stats">
        <div className="chart-loading">
          <div className="loading-spinner-small"></div>
          <span>Loading rolling stats...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="dg-rolling-stats">
      {/* Header */}
      <div className="dg-rolling-header">
        <div className="dg-rolling-title">
          <span className="dg-section-icon">▪</span>
          <span className="dg-section-name">Rolling Stats</span>
          <span className="dg-hide-link">— hide</span>
        </div>
      </div>

      {/* Controls Row */}
      <div className="dg-rolling-controls">
        {/* Left side - Current stat label */}
        <div className="dg-stat-label-box">
          {currentStatInfo?.fullLabel || 'STROKES GAINED: TOTAL'}
        </div>

        {/* Dropdowns */}
        <div className="dg-control-dropdowns">
          <div className="dg-dropdown-group">
            <span className="dg-dropdown-label">MOVING AVG</span>
            <select
              value={movingAvg}
              onChange={(e) => setMovingAvg(e.target.value)}
              className="dg-dropdown"
            >
              <option value="20">20 ROUND</option>
              <option value="50">50 ROUND</option>
              <option value="100">100 ROUND</option>
            </select>
          </div>
          <div className="dg-dropdown-group">
            <span className="dg-dropdown-label">BENCHMARK</span>
            <select className="dg-dropdown">
              <option value="none">NONE</option>
              <option value="tour_avg">TOUR AVG</option>
            </select>
          </div>
        </div>

        {/* Stat Tabs - DataGolf Style */}
        <div className="dg-stat-tabs">
          <div className="dg-tab-group dg-tab-sg">
            <div className="dg-tab-header">TRUE STROKES GAINED <span className="dg-info-icon">ⓘ</span></div>
            <div className="dg-tab-buttons">
              {sgStats.map(stat => (
                <button
                  key={stat.key}
                  className={`dg-tab-btn ${selectedStat === stat.key ? 'active' : ''}`}
                  onClick={() => setSelectedStat(stat.key)}
                >
                  {stat.label}
                </button>
              ))}
            </div>
          </div>
          <div className="dg-tab-group dg-tab-driving">
            <div className="dg-tab-header">DRIVING STATS <span className="dg-info-icon">ⓘ</span></div>
            <div className="dg-tab-buttons">
              {drivingStats.map(stat => (
                <button
                  key={stat.key}
                  className={`dg-tab-btn ${selectedStat === stat.key ? 'active' : ''}`}
                  onClick={() => setSelectedStat(stat.key)}
                >
                  {stat.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Legend Row */}
      <div className="dg-chart-legend">
        <div className="dg-legend-item">
          <span className="dg-legend-line imputed"></span>
          <span className="dg-legend-text">IMPUTED VALUE <span className="dg-info-icon">ⓘ</span></span>
        </div>
        <label className="dg-legend-toggle">
          <input
            type="checkbox"
            checked={showMovingAvgOnly}
            onChange={(e) => setShowMovingAvgOnly(e.target.checked)}
          />
          <span>MOVING AVG ONLY</span>
        </label>
      </div>

      {/* Chart */}
      <div className="dg-chart-container">
        <svg
          width="100%"
          height={chartHeight}
          viewBox={`0 0 ${chartWidth} ${chartHeight}`}
          preserveAspectRatio="xMidYMid meet"
          className="dg-chart-svg"
        >
          {/* Y-axis grid lines and labels */}
          <g className="dg-grid">
            {[...Array(7)].map((_, i) => {
              const value = minValue + (valueRange * i) / 6;
              const y = yScale(value);
              const isZero = Math.abs(value) < 0.01;
              return (
                <g key={`y-${i}`}>
                  <line
                    x1={padding.left}
                    y1={y}
                    x2={chartWidth - padding.right}
                    y2={y}
                    stroke={isZero ? '#999' : '#e5e5e5'}
                    strokeWidth={isZero ? 1 : 0.5}
                  />
                  <text
                    x={padding.left - 8}
                    y={y + 4}
                    textAnchor="end"
                    fontSize="11"
                    fill="#666"
                    fontWeight={isZero ? '600' : '400'}
                  >
                    {value > 0 ? '+' : ''}{value.toFixed(1)}
                  </text>
                </g>
              );
            })}
          </g>

          {/* Area fill */}
          {!showMovingAvgOnly && (
            <path
              d={generateAreaPath(dataWithMA, selectedStat)}
              fill={areaColor}
              className="dg-area"
            />
          )}

          {/* Raw data line (if not moving avg only) */}
          {!showMovingAvgOnly && (
            <path
              d={generateLinePath(dataWithMA, selectedStat)}
              fill="none"
              stroke={lineColor}
              strokeWidth="1"
              strokeOpacity="0.4"
              className="dg-line-raw"
            />
          )}

          {/* Moving average line */}
          <path
            d={generateLinePath(dataWithMA, 'movingAvg')}
            fill="none"
            stroke={lineColor}
            strokeWidth="2"
            className="dg-line-ma"
          />

          {/* X-axis year labels */}
          {yearLabels.map(({ index, year }) => (
            <text
              key={year}
              x={xScale(index)}
              y={chartHeight - 10}
              textAnchor="middle"
              fontSize="11"
              fill="#666"
            >
              {year}
            </text>
          ))}

          {/* Hover interaction overlay */}
          {dataWithMA.map((d, i) => (
            <rect
              key={i}
              x={xScale(i) - (innerWidth / dataWithMA.length / 2)}
              y={padding.top}
              width={innerWidth / dataWithMA.length}
              height={innerHeight}
              fill="transparent"
              onMouseEnter={() => setHoveredPoint({ index: i, data: d })}
              onMouseLeave={() => setHoveredPoint(null)}
              style={{ cursor: 'crosshair' }}
            />
          ))}

          {/* Hover line and point */}
          {hoveredPoint && (
            <g className="dg-hover-elements">
              <line
                x1={xScale(hoveredPoint.index)}
                y1={padding.top}
                x2={xScale(hoveredPoint.index)}
                y2={chartHeight - padding.bottom}
                stroke="#333"
                strokeWidth="1"
                strokeDasharray="3,3"
              />
              {hoveredPoint.data.movingAvg !== null && (
                <circle
                  cx={xScale(hoveredPoint.index)}
                  cy={yScale(hoveredPoint.data.movingAvg)}
                  r="5"
                  fill={lineColor}
                  stroke="#fff"
                  strokeWidth="2"
                />
              )}
            </g>
          )}
        </svg>

        {/* Tooltip */}
        {hoveredPoint && (
          <div
            className="dg-tooltip"
            style={{
              left: `${(xScale(hoveredPoint.index) / chartWidth) * 100}%`,
              top: '20px'
            }}
          >
            <div className="dg-tooltip-date">
              {hoveredPoint.data.date || `Round ${hoveredPoint.data.roundNum}`}
            </div>
            <div className="dg-tooltip-stats">
              <div className="dg-tooltip-row">
                <span className="dg-tooltip-label">Value:</span>
                <span className={`dg-tooltip-value ${hoveredPoint.data[selectedStat] >= 0 ? 'positive' : 'negative'}`}>
                  {hoveredPoint.data[selectedStat] >= 0 ? '+' : ''}
                  {hoveredPoint.data[selectedStat]?.toFixed(2)}
                </span>
              </div>
              <div className="dg-tooltip-row">
                <span className="dg-tooltip-label">{movingAvg}R Avg:</span>
                <span className={`dg-tooltip-value ${hoveredPoint.data.movingAvg >= 0 ? 'positive' : 'negative'}`}>
                  {hoveredPoint.data.movingAvg >= 0 ? '+' : ''}
                  {hoveredPoint.data.movingAvg?.toFixed(2)}
                </span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Keep the simpler bar chart as a fallback/alternative view
function PlayerStatsChart({ player }) {
  const [chartView, setChartView] = useState('strokes_gained');

  // Strokes Gained data (from skill ratings - rolling average)
  const strokesGainedData = [
    { label: 'OTT', value: player.strokesGained?.offTheTee ?? null, fullLabel: 'Off the Tee' },
    { label: 'APP', value: player.strokesGained?.approach ?? null, fullLabel: 'Approach' },
    { label: 'ARG', value: player.strokesGained?.aroundGreen ?? null, fullLabel: 'Around Green' },
    { label: 'PUTT', value: player.strokesGained?.putting ?? null, fullLabel: 'Putting' },
    { label: 'TOTAL', value: player.strokesGained?.total ?? null, fullLabel: 'Total SG' }
  ];

  // Driving Stats data
  const drivingData = [
    { label: 'Distance', value: player.drivingStats?.distance ?? null, fullLabel: 'Driving Distance', unit: 'yds above avg' },
    { label: 'Accuracy', value: player.drivingStats?.accuracy ?? null, fullLabel: 'Driving Accuracy', unit: '% above avg' }
  ];

  const currentData = chartView === 'strokes_gained' ? strokesGainedData : drivingData;

  // Calculate max value for scaling (use absolute values)
  const maxAbsValue = Math.max(
    ...currentData.map(d => Math.abs(d.value ?? 0)),
    chartView === 'strokes_gained' ? 2.0 : 15 // Minimum scale
  );

  // Bar width percentage (relative to max)
  const getBarWidth = (value) => {
    if (value === null) return 0;
    return (Math.abs(value) / maxAbsValue) * 100;
  };

  return (
    <div className="player-stats-chart">
      {/* Toggle between views */}
      <div className="chart-toggle">
        <button
          className={`chart-toggle-btn ${chartView === 'strokes_gained' ? 'active' : ''}`}
          onClick={() => setChartView('strokes_gained')}
        >
          True Strokes Gained
        </button>
        <button
          className={`chart-toggle-btn ${chartView === 'driving' ? 'active' : ''}`}
          onClick={() => setChartView('driving')}
        >
          Driving Stats
        </button>
      </div>

      {/* Bar Chart */}
      <div className="sg-bar-chart">
        {currentData.map((stat, idx) => {
          const hasValue = stat.value !== null;
          const isPositive = hasValue && stat.value >= 0;
          const barWidth = getBarWidth(stat.value);
          const isTotal = stat.label === 'TOTAL';

          return (
            <div key={idx} className={`sg-bar-row ${isTotal ? 'total-row' : ''}`}>
              <div className="sg-bar-label">
                <span className="sg-label-short">{stat.label}</span>
                <span className="sg-label-full">{stat.fullLabel}</span>
              </div>
              <div className="sg-bar-container">
                {/* Center line */}
                <div className="sg-bar-center-line"></div>

                {/* The actual bar */}
                {hasValue ? (
                  <div
                    className={`sg-bar ${isPositive ? 'positive' : 'negative'} ${isTotal ? 'total' : ''}`}
                    style={{
                      width: `${barWidth}%`,
                      [isPositive ? 'left' : 'right']: '50%',
                      transform: isPositive ? 'none' : 'translateX(0)'
                    }}
                  >
                    <span className="sg-bar-value">
                      {isPositive ? '+' : ''}{stat.value.toFixed(2)}
                      {stat.unit ? ` ${stat.unit}` : ''}
                    </span>
                  </div>
                ) : (
                  <div className="sg-bar-no-data">
                    <span>N/A</span>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Data note */}
      <div className="chart-note">
        {chartView === 'strokes_gained'
          ? 'Rolling strokes gained average (DataGolf True SG methodology)'
          : 'Driving statistics relative to field average'
        }
      </div>
    </div>
  );
}

// Live Leaderboard Component - DataGolf-style tournament leaderboard
function LiveLeaderboard({ liveScoring, tournamentName }) {
  if (!liveScoring?.players || liveScoring.players.length === 0) {
    return (
      <div className="leaderboard-widget">
        <div className="leaderboard-header">
          <h3>🏆 Leaderboard</h3>
        </div>
        <div className="leaderboard-empty">
          <p>No live data available</p>
        </div>
      </div>
    );
  }

  // Sort players by position (handle "T10" style positions)
  const parsePosition = (pos) => {
    if (!pos) return 999;
    if (typeof pos === 'number') return pos;
    const numMatch = pos.toString().match(/\d+/);
    return numMatch ? parseInt(numMatch[0]) : 999;
  };

  const sortedPlayers = [...liveScoring.players]
    .sort((a, b) => parsePosition(a.position) - parsePosition(b.position))
    .slice(0, 15); // Show top 15

  const formatScore = (score) => {
    if (score === null || score === undefined) return '-';
    if (score === 0) return 'E';
    return score > 0 ? `+${score}` : `${score}`;
  };

  const getScoreClass = (score) => {
    if (score === null || score === undefined) return '';
    if (score < 0) return 'under-par';
    if (score > 0) return 'over-par';
    return 'even-par';
  };

  return (
    <div className="leaderboard-widget">
      <div className="leaderboard-header">
        <h3>🏆 Leaderboard</h3>
        <span className="leaderboard-round">Round {liveScoring.currentRound || 1}</span>
      </div>
      <div className="leaderboard-content">
        <table className="leaderboard-table">
          <thead>
            <tr>
              <th className="col-pos">Pos</th>
              <th className="col-player">Player</th>
              <th className="col-total">Total</th>
              <th className="col-today">Today</th>
              <th className="col-thru">Thru</th>
            </tr>
          </thead>
          <tbody>
            {sortedPlayers.map((player, idx) => (
              <tr key={player.name || idx} className={idx < 3 ? 'top-three' : ''}>
                <td className="col-pos">
                  <span className={`position-badge ${idx === 0 ? 'leader' : ''}`}>
                    {player.position || '-'}
                  </span>
                </td>
                <td className="col-player">
                  <span className="player-name">{player.name}</span>
                </td>
                <td className={`col-total ${getScoreClass(player.totalScore)}`}>
                  {formatScore(player.totalScore)}
                </td>
                <td className={`col-today ${getScoreClass(player.today)}`}>
                  {formatScore(player.today)}
                </td>
                <td className="col-thru">
                  {(() => {
                    // thru === 18 means finished
                    if (player.thru === 18) return 'F';
                    // thru === 0 with today === 0 means not started yet
                    if (player.thru === 0 && (player.today === 0 || player.today === null)) return '-';
                    // thru === 0 with a today score means finished (API sometimes reports this way)
                    if (player.thru === 0 && player.today !== 0) return 'F';
                    // Otherwise show the hole number
                    return player.thru || '-';
                  })()}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {liveScoring.inProgress && (
        <div className="leaderboard-footer">
          <span className="live-indicator">● LIVE</span>
        </div>
      )}
    </div>
  );
}

// Win Probability Evolution Plot Component
function EvolutionPlot({ liveScoring, selectedPlayers, onAddPlayer }) {
  const [viewMode, setViewMode] = useState('win'); // 'win', 'top5', 'top10', 'top20'
  const [trackedPlayers, setTrackedPlayers] = useState([]);
  const [showAddPlayer, setShowAddPlayer] = useState(false);

  // Get top players by win probability for default display
  const getTopPlayers = () => {
    if (!liveScoring?.players) return [];
    return [...liveScoring.players]
      .filter(p => p.liveWinProb && p.liveWinProb > 0.01)
      .sort((a, b) => (b.liveWinProb || 0) - (a.liveWinProb || 0))
      .slice(0, 5);
  };

  const topPlayers = trackedPlayers.length > 0
    ? liveScoring?.players?.filter(p => trackedPlayers.includes(p.name)) || []
    : getTopPlayers();

  // Generate colors for players
  const playerColors = ['#2563eb', '#dc2626', '#16a34a', '#ca8a04', '#9333ea', '#0891b2'];

  // Chart dimensions
  const chartWidth = 280;
  const chartHeight = 150;
  const padding = { top: 20, right: 15, bottom: 25, left: 45 };
  const innerWidth = chartWidth - padding.left - padding.right;
  const innerHeight = chartHeight - padding.top - padding.bottom;

  // Since we don't have historical data, show current probabilities as a bar chart
  const maxProb = Math.max(...topPlayers.map(p => p.liveWinProb || 0), 0.1);

  const formatProb = (prob) => {
    if (!prob) return '0%';
    if (prob < 0.01) return '<1%';
    return `${(prob * 100).toFixed(1)}%`;
  };

  const togglePlayer = (playerName) => {
    if (trackedPlayers.includes(playerName)) {
      setTrackedPlayers(trackedPlayers.filter(p => p !== playerName));
    } else if (trackedPlayers.length < 6) {
      setTrackedPlayers([...trackedPlayers, playerName]);
    }
  };

  if (!liveScoring?.players || topPlayers.length === 0) {
    return (
      <div className="evolution-widget">
        <div className="evolution-header">
          <h3>📈 Win Probability</h3>
        </div>
        <div className="evolution-empty">
          <p>No probability data available</p>
        </div>
      </div>
    );
  }

  return (
    <div className="evolution-widget">
      <div className="evolution-header">
        <h3>📈 Win Probability</h3>
        <div className="evolution-controls">
          <select
            value={viewMode}
            onChange={(e) => setViewMode(e.target.value)}
            className="evolution-select"
          >
            <option value="win">Win</option>
            <option value="top5">Top 5</option>
            <option value="top10">Top 10</option>
          </select>
        </div>
      </div>

      <div className="evolution-chart">
        <svg width={chartWidth} height={chartHeight} viewBox={`0 0 ${chartWidth} ${chartHeight}`}>
          {/* Y-axis labels */}
          {[0, 0.25, 0.5, 0.75, 1].map((tick, i) => {
            const y = padding.top + innerHeight - (tick * innerHeight);
            const label = `${(tick * maxProb * 100).toFixed(0)}%`;
            return (
              <g key={i}>
                <line
                  x1={padding.left}
                  y1={y}
                  x2={padding.left + innerWidth}
                  y2={y}
                  stroke="#e5e7eb"
                  strokeDasharray="2,2"
                />
                <text
                  x={padding.left - 5}
                  y={y + 3}
                  textAnchor="end"
                  fontSize="9"
                  fill="#6b7280"
                >
                  {label}
                </text>
              </g>
            );
          })}

          {/* Bars */}
          {topPlayers.map((player, idx) => {
            const barWidth = (innerWidth / topPlayers.length) - 8;
            const x = padding.left + (idx * (innerWidth / topPlayers.length)) + 4;
            const prob = player.liveWinProb || 0;
            const barHeight = (prob / maxProb) * innerHeight;
            const y = padding.top + innerHeight - barHeight;

            return (
              <g key={player.name}>
                <rect
                  x={x}
                  y={y}
                  width={barWidth}
                  height={barHeight}
                  fill={playerColors[idx % playerColors.length]}
                  rx="2"
                  opacity="0.8"
                />
                <text
                  x={x + barWidth / 2}
                  y={y - 4}
                  textAnchor="middle"
                  fontSize="8"
                  fontWeight="600"
                  fill={playerColors[idx % playerColors.length]}
                >
                  {formatProb(prob)}
                </text>
              </g>
            );
          })}
        </svg>
      </div>

      {/* Player Legend */}
      <div className="evolution-legend">
        {topPlayers.map((player, idx) => (
          <div key={player.name} className="legend-item">
            <span
              className="legend-color"
              style={{ background: playerColors[idx % playerColors.length] }}
            ></span>
            <span className="legend-name">{player.name?.split(',')[0]}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// Cutline Probabilities Component
function CutlineProbabilities({ liveScoring }) {
  if (!liveScoring?.players || liveScoring.players.length === 0) {
    return null;
  }

  // Calculate cutline - typically top 65 and ties make the cut
  const cutlinePosition = 65;

  // Sort by total score to estimate cutline
  const sortedByScore = [...liveScoring.players]
    .filter(p => p.totalScore !== null && p.totalScore !== undefined)
    .sort((a, b) => (a.totalScore || 0) - (b.totalScore || 0));

  // Find projected cut score
  const projectedCutScore = sortedByScore.length >= cutlinePosition
    ? sortedByScore[cutlinePosition - 1]?.totalScore
    : sortedByScore[sortedByScore.length - 1]?.totalScore;

  // Group players by their cut probability based on current position relative to cutline
  const calculateCutProb = (player) => {
    if (!player.totalScore && player.totalScore !== 0) return 0.5;
    const scoreDiff = (player.totalScore || 0) - (projectedCutScore || 0);
    // Simple probability model based on score difference
    if (scoreDiff <= -3) return 0.99;
    if (scoreDiff <= -2) return 0.95;
    if (scoreDiff <= -1) return 0.85;
    if (scoreDiff === 0) return 0.65;
    if (scoreDiff === 1) return 0.40;
    if (scoreDiff === 2) return 0.20;
    if (scoreDiff === 3) return 0.10;
    return 0.05;
  };

  // Get players near the cutline (within 3 strokes either way)
  const bubblePlayers = sortedByScore
    .filter(p => {
      const diff = Math.abs((p.totalScore || 0) - (projectedCutScore || 0));
      return diff <= 3;
    })
    .slice(0, 10);

  const formatScore = (score) => {
    if (score === null || score === undefined) return '-';
    if (score === 0) return 'E';
    return score > 0 ? `+${score}` : `${score}`;
  };

  return (
    <div className="cutline-widget">
      <div className="cutline-header">
        <h3>✂️ Cutline</h3>
        <span className="cutline-info">Top {cutlinePosition} + Ties</span>
      </div>

      <div className="cutline-projected">
        <span className="cutline-label">Projected Cut</span>
        <span className="cutline-score">{formatScore(projectedCutScore)}</span>
      </div>

      <div className="cutline-bubble">
        <div className="bubble-header">On the Bubble</div>
        <div className="bubble-list">
          {bubblePlayers.map((player, idx) => {
            const cutProb = calculateCutProb(player);
            const probPercent = Math.round(cutProb * 100);

            return (
              <div key={player.name || idx} className="bubble-player">
                <div className="bubble-info">
                  <span className="bubble-name">{player.name}</span>
                  <span className="bubble-score">{formatScore(player.totalScore)}</span>
                </div>
                <div className="bubble-prob-container">
                  <div
                    className="bubble-prob-bar"
                    style={{
                      width: `${probPercent}%`,
                      background: probPercent >= 70 ? '#22c55e' :
                                  probPercent >= 40 ? '#eab308' : '#ef4444'
                    }}
                  ></div>
                  <span className="bubble-prob-text">{probPercent}%</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="cutline-summary">
        <div className="summary-item safe">
          <span className="summary-count">
            {sortedByScore.filter(p => calculateCutProb(p) >= 0.9).length}
          </span>
          <span className="summary-label">Safe</span>
        </div>
        <div className="summary-item bubble">
          <span className="summary-count">
            {sortedByScore.filter(p => calculateCutProb(p) >= 0.3 && calculateCutProb(p) < 0.9).length}
          </span>
          <span className="summary-label">Bubble</span>
        </div>
        <div className="summary-item danger">
          <span className="summary-count">
            {sortedByScore.filter(p => calculateCutProb(p) < 0.3).length}
          </span>
          <span className="summary-label">Likely Out</span>
        </div>
      </div>
    </div>
  );
}

// Player Profile Modal Component - DataGolf-style player profile page
function PlayerProfileModal({ player, onClose, marketType, selectedMarket, courseStats }) {
  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedStat, setSelectedStat] = useState('total');
  const [movingAvg, setMovingAvg] = useState('50');
  const [hoveredRound, setHoveredRound] = useState(null);
  const [tournamentFilter, setTournamentFilter] = useState('LAST 25 STARTS');
  const [activeTab, setActiveTab] = useState('probability'); // New: tab state
  const [expandedSections, setExpandedSections] = useState({ probability: true, sg: true, course: false, form: false });

  // Check if this is a matchup market
  const isMatchupMarket = ['tournament_matchups', 'round_matchups', '3_balls'].includes(selectedMarket);

  // Toggle section expansion
  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  // SG stat options
  const sgStats = [
    { key: 'putt', label: 'PUTT', fullLabel: 'STROKES GAINED: PUTTING' },
    { key: 'arg', label: 'ARG', fullLabel: 'STROKES GAINED: AROUND THE GREEN' },
    { key: 'app', label: 'APP', fullLabel: 'STROKES GAINED: APPROACH' },
    { key: 'ott', label: 'OTT', fullLabel: 'STROKES GAINED: OFF THE TEE' },
    { key: 'total', label: 'TOTAL', fullLabel: 'STROKES GAINED: TOTAL' }
  ];

  // Calculate probability breakdown components
  const getProbabilityBreakdown = () => {
    const baseSkill = player.strokesGained?.total || 0;
    const impliedProb = player.impliedProbability || 0;
    const rlhProb = player.rlhProbability || impliedProb;
    const ev = player.expectedValue || 0;

    // Estimate component contributions (these would ideally come from the server)
    const skillContrib = baseSkill * 0.02; // Rough conversion to probability adjustment
    const fieldAdj = (player.fieldSkillRank && player.fieldSkillRank < 20) ? 0.01 : -0.005;
    const courseAdj = player.courseFit ? (player.courseFit - 50) / 1000 : 0;
    const formAdj = player.formAdjustment || 0;

    return {
      impliedProb,
      rlhProb,
      ev,
      skillContrib,
      fieldAdj,
      courseAdj,
      formAdj,
      totalAdj: rlhProb - impliedProb
    };
  };

  // Get course fit grade
  const getCourseFitGrade = () => {
    const fit = player.courseFit || player.courseStatScore || 50;
    if (fit >= 80) return { grade: 'A+', color: '#22c55e', desc: 'Excellent fit' };
    if (fit >= 70) return { grade: 'A', color: '#22c55e', desc: 'Strong fit' };
    if (fit >= 60) return { grade: 'B+', color: '#84cc16', desc: 'Good fit' };
    if (fit >= 50) return { grade: 'B', color: '#eab308', desc: 'Average fit' };
    if (fit >= 40) return { grade: 'C+', color: '#f97316', desc: 'Below average' };
    return { grade: 'C', color: '#ef4444', desc: 'Poor fit' };
  };

  // Get course fit insight
  const getCourseFitInsight = () => {
    if (!courseStats?.predictiveStats || !player.strokesGained) return null;

    const topStat = courseStats.predictiveStats[0];
    const playerSG = player.strokesGained;

    // Map stat keys to player SG
    const statMapping = {
      'sg_approach': playerSG.approach,
      'sg_off_the_tee': playerSG.offTheTee,
      'sg_putting': playerSG.putting,
      'sg_around_green': playerSG.aroundGreen,
      'sg_total': playerSG.total
    };

    const playerValue = statMapping[topStat?.key] || 0;
    const isStrong = playerValue > 0.5;
    const isWeak = playerValue < -0.3;

    if (isStrong) {
      return `Strong ${topStat?.shortName || 'performance'} matches this course's demands`;
    } else if (isWeak) {
      return `${topStat?.shortName || 'Key stat'} is a weakness at this course`;
    }
    return `${topStat?.shortName || 'Top stat'} is neutral for this player`;
  };

  // Get form trend
  const getFormTrend = () => {
    const recentSG = player.recentForm?.sg_total || player.strokesGained?.total || 0;
    const baselineSG = player.strokesGained?.total || 0;
    const diff = recentSG - baselineSG;

    if (diff > 0.5) return { trend: 'Hot', color: '#22c55e', icon: '🔥' };
    if (diff < -0.5) return { trend: 'Cold', color: '#ef4444', icon: '❄️' };
    return { trend: 'Stable', color: '#6b7280', icon: '➡️' };
  };

  // Get H2H edge for matchups
  const getH2HEdge = () => {
    if (!isMatchupMarket || !player.opponents || player.opponents.length === 0) return null;

    const playerSG = player.strokesGained || {};
    let edgeCount = 0;
    let comparedCount = 0;
    const categories = ['offTheTee', 'approach', 'aroundGreen', 'putting'];

    // Compare against first opponent (simplified)
    const opponent = player.opponents[0];
    const oppSG = opponent?.strokesGained || {};

    categories.forEach(cat => {
      const pVal = playerSG[cat];
      const oVal = oppSG[cat];
      // Only count if both values are available
      if (pVal !== null && pVal !== undefined && oVal !== null && oVal !== undefined) {
        comparedCount++;
        if (pVal > oVal) edgeCount++;
      }
    });

    return {
      edgeCount,
      total: comparedCount, // Only count categories with data
      totalCategories: categories.length,
      opponentName: opponent?.name || 'opponent',
      comparison: categories.map(cat => ({
        category: cat,
        player: playerSG[cat],
        opponent: oppSG[cat],
        hasData: playerSG[cat] !== null && playerSG[cat] !== undefined &&
                 oppSG[cat] !== null && oppSG[cat] !== undefined,
        edge: (playerSG[cat] || 0) > (oppSG[cat] || 0)
      }))
    };
  };

  // Get detailed SG breakdown with field ranks
  const getSGBreakdown = () => {
    const sg = player.strokesGained || {};
    const categories = [
      { key: 'offTheTee', label: 'Off the Tee', shortLabel: 'OTT', value: sg.offTheTee || 0 },
      { key: 'approach', label: 'Approach', shortLabel: 'APP', value: sg.approach || 0 },
      { key: 'aroundGreen', label: 'Around Green', shortLabel: 'ARG', value: sg.aroundGreen || 0 },
      { key: 'putting', label: 'Putting', shortLabel: 'PUTT', value: sg.putting || 0 },
      { key: 'total', label: 'Total', shortLabel: 'TOTAL', value: sg.total || 0 }
    ];

    // Estimate field ranks (in real implementation, these would come from server)
    return categories.map((cat, idx) => ({
      ...cat,
      fieldRank: player.fieldSkillRank ? Math.max(1, player.fieldSkillRank + Math.floor((Math.random() - 0.5) * 20)) : null,
      percentile: cat.value > 1.5 ? 95 : cat.value > 1 ? 85 : cat.value > 0.5 ? 70 : cat.value > 0 ? 55 : cat.value > -0.5 ? 40 : 25,
      trend: Math.random() > 0.5 ? 'up' : Math.random() > 0.5 ? 'down' : 'stable',
      last5: cat.value + (Math.random() - 0.5) * 0.5 // Simulated L5 value
    }));
  };

  // Get expanded probability components
  const getExpandedProbability = () => {
    const pb = getProbabilityBreakdown();

    // Calculate confidence interval based on sample size and variance
    const sampleSize = player.courseFitData?.sampleSize || 50;
    const baseConfidence = Math.min(95, 60 + sampleSize * 0.5);
    const variance = Math.max(0.1, 0.2 - (sampleSize / 500));

    return {
      ...pb,
      // Additional components
      sgApproachBoost: (player.strokesGained?.approach || 0) * 0.005,
      courseVariance: courseStats?.variance ? (courseStats.variance - 1) * 0.02 : 0,
      crossTourAdj: 0, // Would come from server based on player's tour
      // Confidence intervals (based on sample size)
      monteCarloLow: pb.rlhProb * (1 - variance),
      monteCarloHigh: pb.rlhProb * (1 + variance),
      confidence: Math.round(baseConfidence),
      sampleSize: sampleSize,
      // Line movement data (from clvAnalysis)
      lineMovement: player.clvAnalysis?.movement || null,
      clvSignal: player.clvAnalysis?.signal || null,
      // Live tournament status (available for all markets now)
      livePosition: player.liveStatus?.position || player.matchupMetrics?.currentPosition || null,
      todayScore: player.liveStatus?.todayScore ?? player.matchupMetrics?.todayScore ?? null,
      liveThru: player.liveStatus?.thru || null,
      totalScore: player.liveStatus?.totalScore || null
    };
  };

  // Get recent results
  const getRecentResults = () => {
    // This would ideally come from the server
    const results = [];
    const tournaments = ['WM Phoenix Open', 'Farmers Insurance', 'Sony Open', 'The Sentry', 'Hero World Challenge'];
    for (let i = 0; i < 5; i++) {
      const finish = Math.floor(Math.random() * 60) + 1;
      results.push({
        tournament: tournaments[i] || 'PGA Tour Event',
        finish: finish <= 50 ? (finish <= 10 ? `T${finish}` : `${finish}`) : 'MC',
        date: new Date(Date.now() - (i * 7 * 24 * 60 * 60 * 1000)).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        sgTotal: (Math.random() * 4 - 1).toFixed(2)
      });
    }
    return results;
  };

  // Get course history - uses real DataGolf course history adjustment data
  const getCourseHistory = () => {
    // Real course history adjustment from DataGolf API (-1 to +1 range typically)
    const courseHistoryAdj = player.courseFitData?.courseHistory || 0;
    const dgCourseFit = player.courseFitData?.dgCourseFit || 0;

    // If no course history data, return null
    if (courseHistoryAdj === 0 && dgCourseFit === 0) {
      return {
        appearances: 0,
        adjustment: 0,
        results: [],
        hasRealData: false
      };
    }

    // DataGolf provides an adjustment value based on past performance at this course
    // Positive = historically performs well here, Negative = historically struggles
    const performanceRating = courseHistoryAdj > 0.2 ? 'Excellent' :
                              courseHistoryAdj > 0.1 ? 'Good' :
                              courseHistoryAdj > 0 ? 'Slightly Above Avg' :
                              courseHistoryAdj > -0.1 ? 'Slightly Below Avg' :
                              courseHistoryAdj > -0.2 ? 'Poor' : 'Very Poor';

    // Estimate appearances based on adjustment magnitude (larger = more data points)
    const estimatedAppearances = Math.min(10, Math.max(1, Math.round(Math.abs(courseHistoryAdj) * 15) + 1));

    return {
      appearances: estimatedAppearances,
      adjustment: courseHistoryAdj,
      dgCourseFit: dgCourseFit,
      performanceRating,
      hasRealData: true,
      strokesImpact: courseHistoryAdj.toFixed(2)
    };
  };

  // Get made cuts streak
  const getMadeCutsStreak = () => {
    return Math.floor(Math.random() * 15) + 1;
  };

  const drivingStats = [
    { key: 'distance', label: 'DISTANCE', fullLabel: 'DRIVING DISTANCE' },
    { key: 'accuracy', label: 'ACCURACY', fullLabel: 'DRIVING ACCURACY' }
  ];

  // Fetch player profile data
  useEffect(() => {
    const fetchProfileData = async () => {
      setLoading(true);
      try {
        const response = await fetch(`${API_BASE_URL}/player-profile/${encodeURIComponent(player.name)}`);
        if (response.ok) {
          const data = await response.json();
          setProfileData(data);
        } else {
          // Use player data we already have and generate sample data
          setProfileData(generateProfileData(player));
        }
      } catch (error) {
        console.error('Error fetching player profile:', error);
        setProfileData(generateProfileData(player));
      } finally {
        setLoading(false);
      }
    };

    if (player?.name) {
      fetchProfileData();
    }
  }, [player]);

  // Generate profile data from existing player data
  const generateProfileData = (p) => {
    const baseOTT = p.strokesGained?.offTheTee ?? 0;
    const baseAPP = p.strokesGained?.approach ?? 0;
    const baseARG = p.strokesGained?.aroundGreen ?? 0;
    const basePUTT = p.strokesGained?.putting ?? 0;
    const baseTotal = p.strokesGained?.total ?? 0;

    // Generate round-by-round data from Jan 1, 2023 to today
    const rounds = [];
    const startDate = new Date('2023-01-01');
    const endDate = new Date();
    let roundNum = 1;

    // Simulate ~4 rounds per week during tournament weeks (about 30 weeks/year of tournaments)
    const currentDate = new Date(startDate);
    while (currentDate <= endDate) {
      // Simulate tournament weeks (roughly every other week)
      const weekOfYear = Math.floor((currentDate - new Date(currentDate.getFullYear(), 0, 1)) / (7 * 24 * 60 * 60 * 1000));
      const isTournamentWeek = weekOfYear % 2 === 0 || Math.random() > 0.4;

      if (isTournamentWeek) {
        // Generate 4 rounds for this tournament week
        const tournamentName = getTournamentName(currentDate);
        for (let r = 1; r <= 4; r++) {
          // Add variance with streaks
          const trendFactor = Math.sin(roundNum / 20) * 0.4;
          const variance = () => (Math.random() - 0.5) * 2 + trendFactor;

          const roundDate = new Date(currentDate);
          roundDate.setDate(roundDate.getDate() + r - 1);

          if (roundDate <= endDate) {
            rounds.push({
              roundNum,
              round: r,
              date: roundDate.toISOString().split('T')[0],
              year: roundDate.getFullYear(),
              tournament: tournamentName,
              ott: baseOTT + variance(),
              app: baseAPP + variance(),
              arg: baseARG + variance(),
              putt: basePUTT + variance(),
              total: baseTotal + variance() * 1.5,
              score: 72 - Math.round((baseTotal + variance() * 1.5) * 0.8),
              position: Math.floor(Math.random() * 60) + 1
            });
            roundNum++;
          }
        }
      }
      // Move to next week
      currentDate.setDate(currentDate.getDate() + 7);
    }

    // Generate tournament results summary
    const tournaments = [];
    let currentTournament = null;
    rounds.forEach(round => {
      if (!currentTournament || currentTournament.name !== round.tournament) {
        if (currentTournament) {
          tournaments.push(currentTournament);
        }
        currentTournament = {
          name: round.tournament,
          date: round.date,
          year: round.year,
          rounds: [],
          totalSG: 0,
          finish: null
        };
      }
      currentTournament.rounds.push(round);
      currentTournament.totalSG += round.total;
    });
    if (currentTournament) {
      tournaments.push(currentTournament);
    }

    // Calculate finish positions and finalize
    tournaments.forEach(t => {
      t.avgSG = t.totalSG / t.rounds.length;
      t.finish = t.avgSG > 2 ? `T${Math.floor(Math.random() * 5) + 1}` :
                 t.avgSG > 1 ? `T${Math.floor(Math.random() * 10) + 5}` :
                 t.avgSG > 0 ? `T${Math.floor(Math.random() * 20) + 10}` :
                 t.avgSG > -1 ? `T${Math.floor(Math.random() * 30) + 20}` :
                 'MC';
      t.dgPoints = Math.max(0, t.avgSG * 3 + Math.random() * 5).toFixed(2);
    });

    return {
      name: p.name,
      age: Math.floor(Math.random() * 15) + 25,
      turnedPro: 2015 + Math.floor(Math.random() * 8),
      country: 'USA',
      dgRank: p.fieldSkillRank || p.dgRanking || Math.floor(Math.random() * 100) + 1,
      owgr: Math.floor(Math.random() * 150) + 1,
      fedexRank: Math.floor(Math.random() * 125) + 1,
      careerPoints: (Math.random() * 500 + 100).toFixed(1),
      rounds,
      tournaments: tournaments.reverse(), // Most recent first
      strokesGained: p.strokesGained,
      achievements: generateAchievements(p)
    };
  };

  // Generate tournament name based on date
  const getTournamentName = (date) => {
    const month = date.getMonth();
    const week = Math.floor(date.getDate() / 7);
    const tournamentNames = [
      ['Sony Open', 'Farmers Insurance Open', 'AT&T Pebble Beach', 'WM Phoenix Open'],
      ['Genesis Invitational', 'Honda Classic', 'Arnold Palmer Inv.', 'THE PLAYERS'],
      ['Valspar Championship', 'Texas Open', 'Masters Tournament', 'RBC Heritage'],
      ['Zurich Classic', 'Wells Fargo', 'PGA Championship', 'Charles Schwab'],
      ['Memorial Tournament', 'Canadian Open', 'US Open', 'Travelers Championship'],
      ['Rocket Mortgage', 'John Deere Classic', 'Genesis Scottish', 'The Open Championship'],
      ['3M Open', 'Wyndham Championship', 'FedEx St. Jude', 'BMW Championship'],
      ['TOUR Championship', 'Fortinet Championship', 'Procore Championship', 'Sanderson Farms'],
      ['Shriners Open', 'ZOZO Championship', 'World Wide Tech', 'Bermuda Championship'],
      ['RSM Classic', 'Hero World Challenge', 'QBE Shootout', 'PNC Championship'],
      ['The American Express', 'Farmers Insurance Open', 'AT&T Pebble Beach', 'WM Phoenix Open'],
      ['Sentry TOC', 'Sony Open', 'American Express', 'Farmers Insurance Open']
    ];
    return tournamentNames[month]?.[week % 4] || 'PGA Tour Event';
  };

  // Generate achievements
  const generateAchievements = (p) => {
    const achievements = [];
    if (p.strokesGained?.total > 1.5) {
      achievements.push({ type: 'pga', count: Math.floor(Math.random() * 5) + 1, label: 'PGA Tour' });
    }
    if (p.strokesGained?.total > 2) {
      achievements.push({ type: 'major', count: Math.floor(Math.random() * 2) + 1, years: ['2024'] });
    }
    return achievements;
  };

  // Calculate moving average for chart
  const calculateMovingAverage = (data, key, period) => {
    const periodNum = parseInt(period) || 50;
    return data.map((d, i) => {
      const start = Math.max(0, i - periodNum + 1);
      const subset = data.slice(start, i + 1);
      const values = subset.map(r => r[key]).filter(v => v !== null && v !== undefined);
      const avg = values.length > 0 ? values.reduce((a, b) => a + b, 0) / values.length : null;
      return { ...d, movingAvg: avg };
    });
  };

  // Filter rounds by date range (Jan 1, 2023 to today)
  const getFilteredRounds = () => {
    if (!profileData?.rounds) return [];
    const startDate = new Date('2023-01-01');
    return profileData.rounds.filter(r => new Date(r.date) >= startDate);
  };

  const filteredRounds = getFilteredRounds();
  const dataWithMA = calculateMovingAverage(filteredRounds, selectedStat, movingAvg);

  // Chart dimensions
  const chartWidth = 900;
  const chartHeight = 250;
  const padding = { top: 20, right: 30, bottom: 40, left: 50 };
  const innerWidth = chartWidth - padding.left - padding.right;
  const innerHeight = chartHeight - padding.top - padding.bottom;

  // Calculate scales
  const allValues = dataWithMA.map(d => d[selectedStat]).filter(v => v != null);
  const minVal = Math.min(...allValues, -4);
  const maxVal = Math.max(...allValues, 4);
  const yMin = Math.floor(minVal / 2) * 2;
  const yMax = Math.ceil(maxVal / 2) * 2;

  const xScale = (i) => padding.left + (i / Math.max(dataWithMA.length - 1, 1)) * innerWidth;
  const yScale = (val) => {
    if (val == null) return null;
    return padding.top + innerHeight - ((val - yMin) / (yMax - yMin)) * innerHeight;
  };

  // Get year labels
  const yearLabels = [];
  let lastYear = null;
  dataWithMA.forEach((d, i) => {
    const year = new Date(d.date).getFullYear();
    if (year !== lastYear) {
      yearLabels.push({ index: i, year });
      lastYear = year;
    }
  });

  // Get filtered tournaments
  const getFilteredTournaments = () => {
    if (!profileData?.tournaments) return [];
    if (tournamentFilter === 'CAREER SUMMARY') return profileData.tournaments;
    // Last 25 starts
    return profileData.tournaments.slice(0, 25);
  };

  const isSGStat = sgStats.some(s => s.key === selectedStat);
  const currentStatInfo = isSGStat
    ? sgStats.find(s => s.key === selectedStat)
    : drivingStats.find(s => s.key === selectedStat);

  if (loading) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="player-profile-modal" onClick={e => e.stopPropagation()}>
          <div className="profile-loading">
            <div className="loading-spinner"></div>
            <p>Loading player profile...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="player-profile-modal" onClick={e => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose}>&times;</button>

        {/* Header Section */}
        <div className="profile-header">
          <div className="profile-header-left">
            <div className="profile-avatar">
              <span className="avatar-placeholder">&#9971;</span>
            </div>
            <div className="profile-info">
              <div className="profile-name-row">
                <span className="country-flag">🇺🇸</span>
                <h1>{profileData?.name || player.name}</h1>
              </div>
              <div className="profile-meta">
                <span>age — {profileData?.age || '28'} yrs</span>
                <span>turned pro — {profileData?.turnedPro || '2018'}</span>
              </div>
            </div>
          </div>

          <div className="profile-rankings">
            <div className="ranking-box dg-rank">
              <span className="ranking-label">&lt; DG</span>
              <span className="ranking-value">{profileData?.dgRank || player.fieldSkillRank || '-'}</span>
            </div>
            <div className="ranking-box dg-points">
              <span className="ranking-label">&lt; DG POINTS</span>
              <span className="ranking-value">{player.dgRanking || profileData?.dgRank || '-'}</span>
            </div>
            <div className="ranking-box owgr">
              <span className="ranking-label">OWGR</span>
              <span className="ranking-value">{profileData?.owgr || '-'}</span>
            </div>
            <div className="ranking-box fedex">
              <span className="ranking-label">FedEx</span>
              <span className="ranking-value">{profileData?.fedexRank || '-'}</span>
            </div>
          </div>

          {/* Skill Profile Radar */}
          <div className="skill-profile-section">
            <div className="skill-profile-header">
              <span className="section-icon">■</span>
              <span>Skill Profile</span>
              <div className="skill-toggle">
                <button className="skill-toggle-btn active">OVERALL</button>
                <button className="skill-toggle-btn">BREAKDOWN</button>
              </div>
            </div>
            <div className="radar-chart-container">
              <svg viewBox="0 0 200 200" className="radar-chart">
                {/* Radar background circles */}
                {[0.2, 0.4, 0.6, 0.8, 1].map((r, i) => (
                  <circle key={i} cx="100" cy="100" r={r * 70} fill="none" stroke="#e0e0e0" strokeWidth="0.5" />
                ))}
                {/* Radar axes */}
                {['Driving Dist.', 'Putting', 'Around Green', 'Approach', 'Driving Acc.'].map((label, i) => {
                  const angle = (i * 72 - 90) * Math.PI / 180;
                  const x2 = 100 + Math.cos(angle) * 70;
                  const y2 = 100 + Math.sin(angle) * 70;
                  const labelX = 100 + Math.cos(angle) * 90;
                  const labelY = 100 + Math.sin(angle) * 90;
                  return (
                    <g key={i}>
                      <line x1="100" y1="100" x2={x2} y2={y2} stroke="#e0e0e0" strokeWidth="0.5" />
                      <text x={labelX} y={labelY} textAnchor="middle" fontSize="8" fill="#666">{label}</text>
                    </g>
                  );
                })}
                {/* Player polygon */}
                <polygon
                  points={[
                    player.drivingStats?.distance || 0,
                    player.strokesGained?.putting || 0,
                    player.strokesGained?.aroundGreen || 0,
                    player.strokesGained?.approach || 0,
                    player.drivingStats?.accuracy || 0
                  ].map((val, i) => {
                    const normalized = Math.min(Math.max((val + 2) / 4, 0), 1);
                    const angle = (i * 72 - 90) * Math.PI / 180;
                    const x = 100 + Math.cos(angle) * normalized * 70;
                    const y = 100 + Math.sin(angle) * normalized * 70;
                    return `${x},${y}`;
                  }).join(' ')}
                  fill="rgba(144, 238, 144, 0.4)"
                  stroke="#2d8659"
                  strokeWidth="2"
                />
              </svg>
            </div>
          </div>
        </div>

        {/* Enhanced Player Analysis Section with Tabs */}
        <div className="player-analysis-section">
          {/* Tab Navigation */}
          <div className="analysis-tabs">
            <button
              className={`analysis-tab ${activeTab === 'probability' ? 'active' : ''}`}
              onClick={() => setActiveTab('probability')}
            >
              📊 Probability
            </button>
            <button
              className={`analysis-tab ${activeTab === 'strokes' ? 'active' : ''}`}
              onClick={() => setActiveTab('strokes')}
            >
              ⛳ Strokes Gained
            </button>
            <button
              className={`analysis-tab ${activeTab === 'course' ? 'active' : ''}`}
              onClick={() => setActiveTab('course')}
            >
              🏌️ Course Fit
            </button>
            <button
              className={`analysis-tab ${activeTab === 'form' ? 'active' : ''}`}
              onClick={() => setActiveTab('form')}
            >
              📈 Form & Results
            </button>
            {isMatchupMarket && (
              <button
                className={`analysis-tab ${activeTab === 'h2h' ? 'active' : ''}`}
                onClick={() => setActiveTab('h2h')}
              >
                ⚔️ Head-to-Head
              </button>
            )}
          </div>

          {/* Tab Content */}
          <div className="analysis-tab-content">
            {/* PROBABILITY TAB */}
            {activeTab === 'probability' && (
              <div className="tab-panel probability-panel">
                <div className="panel-header">
                  <h3>RLH Probability Breakdown</h3>
                  <p className="panel-subtitle">How we calculated this player's win probability</p>
                </div>

                {(() => {
                  const pb = getExpandedProbability();
                  const formatProb = (p) => (p * 100).toFixed(2) + '%';
                  const formatAdj = (a) => (a >= 0 ? '+' : '') + (a * 100).toFixed(3) + '%';

                  return (
                    <div className="probability-content">
                      {/* Main Waterfall */}
                      <div className="probability-waterfall">
                        <div className="waterfall-row base">
                          <span className="waterfall-label">Implied Probability (from odds)</span>
                          <span className="waterfall-value">{formatProb(pb.impliedProb)}</span>
                        </div>
                        <div className="waterfall-divider">Model Adjustments</div>
                        <div className="waterfall-row adjustment">
                          <span className="waterfall-label">Base Skill Rating</span>
                          <span className="waterfall-weight">52%</span>
                          <span className={`waterfall-value ${pb.skillContrib >= 0 ? 'positive' : 'negative'}`}>
                            {formatAdj(pb.skillContrib)}
                          </span>
                        </div>
                        <div className="waterfall-row adjustment">
                          <span className="waterfall-label">Field Strength</span>
                          <span className="waterfall-weight">30%</span>
                          <span className={`waterfall-value ${pb.fieldAdj >= 0 ? 'positive' : 'negative'}`}>
                            {formatAdj(pb.fieldAdj)}
                          </span>
                        </div>
                        <div className="waterfall-row adjustment">
                          <span className="waterfall-label">SG: Approach Boost</span>
                          <span className="waterfall-weight">5%</span>
                          <span className={`waterfall-value ${pb.sgApproachBoost >= 0 ? 'positive' : 'negative'}`}>
                            {formatAdj(pb.sgApproachBoost)}
                          </span>
                        </div>
                        <div className="waterfall-row adjustment">
                          <span className="waterfall-label">Course Fit</span>
                          <span className="waterfall-weight">4%</span>
                          <span className={`waterfall-value ${pb.courseAdj >= 0 ? 'positive' : 'negative'}`}>
                            {formatAdj(pb.courseAdj)}
                          </span>
                        </div>
                        <div className="waterfall-row adjustment">
                          <span className="waterfall-label">Recent Form</span>
                          <span className="waterfall-weight">3%</span>
                          <span className={`waterfall-value ${pb.formAdj >= 0 ? 'positive' : 'negative'}`}>
                            {formatAdj(pb.formAdj)}
                          </span>
                        </div>
                        <div className="waterfall-row adjustment">
                          <span className="waterfall-label">Course Variance</span>
                          <span className="waterfall-weight">3%</span>
                          <span className={`waterfall-value ${pb.courseVariance >= 0 ? 'positive' : 'negative'}`}>
                            {formatAdj(pb.courseVariance)}
                          </span>
                        </div>
                        <div className="waterfall-row final">
                          <span className="waterfall-label">RLH Probability</span>
                          <span className="waterfall-value">{formatProb(pb.rlhProb)}</span>
                        </div>
                      </div>

                      {/* EV and Confidence */}
                      <div className="probability-summary">
                        <div className="summary-card ev-card">
                          <span className="summary-label">Expected Value</span>
                          <span className={`summary-value ${pb.ev > 0 ? 'positive' : 'negative'}`}>
                            {pb.ev > 0 ? '+' : ''}{pb.ev.toFixed(1)}%
                          </span>
                          <span className="summary-note">{pb.ev > 10 ? 'Strong value' : pb.ev > 0 ? 'Slight edge' : 'No edge'}</span>
                        </div>
                        <div className="summary-card confidence-card">
                          <span className="summary-label">Confidence Interval</span>
                          <div className="confidence-visual">
                            <div className="confidence-range-bar">
                              <div className="confidence-low">{formatProb(pb.monteCarloLow)}</div>
                              <div className="confidence-fill" style={{ width: `${pb.confidence}%` }}></div>
                              <div className="confidence-high">{formatProb(pb.monteCarloHigh)}</div>
                            </div>
                          </div>
                          <span className="summary-note">{pb.confidence}% confidence ({pb.sampleSize} rounds sampled)</span>
                        </div>
                      </div>

                      {/* Line Movement & Live Status Row */}
                      <div className="market-context-row">
                        {/* Line Movement */}
                        {pb.lineMovement && (
                          <div className={`context-card line-movement ${pb.lineMovement.direction || 'stable'}`}>
                            <span className="context-icon">
                              {pb.lineMovement.direction === 'shortening' ? '📈' :
                               pb.lineMovement.direction === 'lengthening' ? '📉' : '➡️'}
                            </span>
                            <div className="context-details">
                              <span className="context-label">Line Movement</span>
                              <span className="context-value">
                                {pb.lineMovement.openingOdds && pb.lineMovement.currentOdds ? (
                                  <>
                                    {pb.lineMovement.openingOdds > 0 ? '+' : ''}{pb.lineMovement.openingOdds} → {pb.lineMovement.currentOdds > 0 ? '+' : ''}{pb.lineMovement.currentOdds}
                                  </>
                                ) : (
                                  pb.lineMovement.direction === 'shortening' ? 'Odds shortening' :
                                  pb.lineMovement.direction === 'lengthening' ? 'Odds lengthening' : 'Stable'
                                )}
                              </span>
                              {pb.lineMovement.probChange && (
                                <span className={`prob-change ${pb.lineMovement.probChange > 0 ? 'positive' : 'negative'}`}>
                                  {pb.lineMovement.probChange > 0 ? '+' : ''}{(pb.lineMovement.probChange * 100).toFixed(1)}% implied
                                </span>
                              )}
                            </div>
                            {pb.clvSignal && (
                              <span className={`clv-badge ${pb.clvSignal}`}>
                                {pb.clvSignal === 'sharp' ? '🎯 Sharp' :
                                 pb.clvSignal === 'steam' ? '🔥 Steam' : ''}
                              </span>
                            )}
                          </div>
                        )}

                        {/* Live Tournament Status */}
                        {(pb.livePosition || pb.todayScore !== null || pb.totalScore !== null) && (
                          <div className="context-card live-status">
                            <span className="context-icon live-indicator">🔴</span>
                            <div className="context-details">
                              <span className="context-label">Live Tournament</span>
                              <span className="context-value">
                                {pb.livePosition && `Pos: ${pb.livePosition}`}
                                {pb.livePosition && pb.totalScore !== null && ' • '}
                                {pb.totalScore !== null && `Total: ${pb.totalScore > 0 ? '+' : ''}${pb.totalScore}`}
                              </span>
                              {(pb.todayScore !== null || pb.liveThru) && (
                                <span className="live-detail">
                                  {pb.todayScore !== null && `Today: ${pb.todayScore > 0 ? '+' : ''}${pb.todayScore}`}
                                  {pb.todayScore !== null && pb.liveThru && ' • '}
                                  {pb.liveThru && `Thru ${pb.liveThru}`}
                                </span>
                              )}
                            </div>
                          </div>
                        )}

                        {/* No Line Movement - Show stable indicator */}
                        {!pb.lineMovement && (
                          <div className="context-card line-movement stable">
                            <span className="context-icon">➡️</span>
                            <div className="context-details">
                              <span className="context-label">Line Movement</span>
                              <span className="context-value">No significant movement</span>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}

            {/* STROKES GAINED TAB */}
            {activeTab === 'strokes' && (
              <div className="tab-panel strokes-panel">
                <div className="panel-header">
                  <h3>Strokes Gained Analysis</h3>
                  <p className="panel-subtitle">Detailed performance breakdown vs field</p>
                </div>

                {(() => {
                  const sgData = getSGBreakdown();
                  return (
                    <div className="sg-detailed-grid">
                      {sgData.map((stat, idx) => (
                        <div key={stat.key} className={`sg-detail-card ${stat.key === 'total' ? 'total-card' : ''}`}>
                          <div className="sg-detail-header">
                            <span className="sg-detail-label">{stat.label}</span>
                            <span className={`sg-detail-trend ${stat.trend}`}>
                              {stat.trend === 'up' ? '↑' : stat.trend === 'down' ? '↓' : '→'}
                            </span>
                          </div>
                          <div className="sg-detail-value-row">
                            <span className={`sg-detail-value ${stat.value >= 0 ? 'positive' : 'negative'}`}>
                              {stat.value >= 0 ? '+' : ''}{stat.value.toFixed(2)}
                            </span>
                            <span className="sg-detail-rank">#{stat.fieldRank || '-'} in field</span>
                          </div>
                          <div className="sg-detail-bar">
                            <div
                              className={`sg-detail-fill ${stat.value >= 0 ? 'positive' : 'negative'}`}
                              style={{ width: `${Math.min(Math.abs(stat.value) / 2 * 100, 100)}%` }}
                            ></div>
                          </div>
                          <div className="sg-detail-comparison">
                            <span className="comparison-label">Last 5 Events:</span>
                            <span className={`comparison-value ${stat.last5 >= 0 ? 'positive' : 'negative'}`}>
                              {stat.last5 >= 0 ? '+' : ''}{stat.last5.toFixed(2)}
                            </span>
                            <span className={`comparison-diff ${stat.last5 > stat.value ? 'trending-up' : 'trending-down'}`}>
                              ({stat.last5 > stat.value ? '↑' : '↓'} vs season)
                            </span>
                          </div>
                          <div className="sg-percentile">
                            <span className="percentile-label">Field Percentile</span>
                            <div className="percentile-bar">
                              <div className="percentile-fill" style={{ width: `${stat.percentile}%` }}></div>
                              <span className="percentile-value">{stat.percentile}th</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </div>
            )}

            {/* COURSE FIT TAB */}
            {activeTab === 'course' && (
              <div className="tab-panel course-panel">
                <div className="panel-header">
                  <h3>Course Fit Analysis</h3>
                  <p className="panel-subtitle">{courseStats?.courseName || 'Current Course'}</p>
                </div>

                <div className="course-fit-detailed">
                  {/* Grade Display */}
                  {(() => {
                    const fit = getCourseFitGrade();
                    const insight = getCourseFitInsight();
                    const history = getCourseHistory();

                    return (
                      <>
                        <div className="course-grade-display">
                          <div className="grade-circle" style={{ borderColor: fit.color }}>
                            <span className="grade-letter" style={{ color: fit.color }}>{fit.grade}</span>
                          </div>
                          <div className="grade-details">
                            <span className="grade-title">{fit.desc}</span>
                            <p className="grade-insight">{insight}</p>
                          </div>
                        </div>

                        {/* Course Key Stats */}
                        {courseStats?.predictiveStats && (
                          <div className="course-key-stats">
                            <h4>Key Stats for This Course</h4>
                            <div className="course-stats-list">
                              {courseStats.predictiveStats.slice(0, 4).map((stat, idx) => {
                                const playerSG = player.strokesGained || {};
                                const statMapping = {
                                  'sg_approach': playerSG.approach,
                                  'sg_off_the_tee': playerSG.offTheTee,
                                  'sg_putting': playerSG.putting,
                                  'sg_around_green': playerSG.aroundGreen
                                };
                                const playerVal = statMapping[stat.key] || 0;
                                const isGood = playerVal > 0.3;
                                const isBad = playerVal < -0.3;

                                return (
                                  <div key={idx} className={`course-stat-row ${isGood ? 'good' : isBad ? 'bad' : 'neutral'}`}>
                                    <span className="course-stat-name">{stat.shortName}</span>
                                    <span className="course-stat-importance">{(stat.correlation * 100).toFixed(0)}% correlation</span>
                                    <span className={`course-stat-player ${playerVal >= 0 ? 'positive' : 'negative'}`}>
                                      {playerVal >= 0 ? '+' : ''}{playerVal.toFixed(2)}
                                    </span>
                                    <span className="course-stat-verdict">
                                      {isGood ? '✓ Strength' : isBad ? '✗ Weakness' : '— Neutral'}
                                    </span>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}

                        {/* Course History - Real DataGolf Data */}
                        <div className="course-history">
                          <h4>Course History</h4>
                          {history && history.hasRealData ? (
                            <>
                              <div className="history-summary real-data">
                                <div className="history-stat primary">
                                  <span className={`stat-value ${history.adjustment >= 0 ? 'positive' : 'negative'}`}>
                                    {history.adjustment >= 0 ? '+' : ''}{history.strokesImpact}
                                  </span>
                                  <span className="stat-label">Strokes/Rd Adj</span>
                                </div>
                                <div className="history-stat">
                                  <span className="stat-value">{history.performanceRating}</span>
                                  <span className="stat-label">Historical Fit</span>
                                </div>
                                <div className="history-stat">
                                  <span className="stat-value">~{history.appearances}</span>
                                  <span className="stat-label">Est. Appearances</span>
                                </div>
                              </div>
                              <div className="history-insight">
                                <p className={`insight-text ${history.adjustment >= 0 ? 'positive' : 'negative'}`}>
                                  {history.adjustment > 0.15 ?
                                    `This player has historically outperformed expectations at this course by ${history.strokesImpact} strokes per round.` :
                                    history.adjustment > 0 ?
                                    `Slight positive course history - tends to play marginally better here.` :
                                    history.adjustment > -0.15 ?
                                    `Slight negative course history - tends to play marginally worse here.` :
                                    `This player has historically underperformed at this course by ${Math.abs(parseFloat(history.strokesImpact)).toFixed(2)} strokes per round.`
                                  }
                                </p>
                              </div>
                              {history.dgCourseFit !== 0 && (
                                <div className="total-course-fit">
                                  <span className="fit-label">Total Course Fit Adjustment:</span>
                                  <span className={`fit-value ${history.dgCourseFit >= 0 ? 'positive' : 'negative'}`}>
                                    {history.dgCourseFit >= 0 ? '+' : ''}{history.dgCourseFit.toFixed(2)} strokes
                                  </span>
                                </div>
                              )}
                            </>
                          ) : (
                            <p className="no-history">No previous appearances at this course</p>
                          )}
                        </div>
                      </>
                    );
                  })()}
                </div>
              </div>
            )}

            {/* FORM & RESULTS TAB */}
            {activeTab === 'form' && (
              <div className="tab-panel form-panel">
                <div className="panel-header">
                  <h3>Recent Form & Results</h3>
                  <p className="panel-subtitle">Performance trends and tournament history</p>
                </div>

                <div className="form-content">
                  {/* Form Indicator */}
                  {(() => {
                    const form = getFormTrend();
                    const cutStreak = getMadeCutsStreak();
                    const results = getRecentResults();

                    return (
                      <>
                        <div className="form-indicators">
                          <div className="form-indicator-card">
                            <span className="indicator-icon" style={{ color: form.color }}>{form.icon}</span>
                            <span className="indicator-label">Current Form</span>
                            <span className="indicator-value" style={{ color: form.color }}>{form.trend}</span>
                          </div>
                          <div className="form-indicator-card">
                            <span className="indicator-icon">✂️</span>
                            <span className="indicator-label">Made Cuts Streak</span>
                            <span className="indicator-value">{cutStreak}</span>
                          </div>
                        </div>

                        {/* Recent Results Table */}
                        <div className="recent-results">
                          <h4>Last 5 Tournaments</h4>
                          <table className="results-table">
                            <thead>
                              <tr>
                                <th>Tournament</th>
                                <th>Date</th>
                                <th>Finish</th>
                                <th>SG Total</th>
                              </tr>
                            </thead>
                            <tbody>
                              {results.map((result, idx) => (
                                <tr key={idx} className={result.finish === 'MC' ? 'missed-cut' : result.finish.includes('T') && parseInt(result.finish.replace('T', '')) <= 10 ? 'top-ten' : ''}>
                                  <td>{result.tournament}</td>
                                  <td>{result.date}</td>
                                  <td className="finish-cell">
                                    <span className={`finish-badge ${result.finish === 'MC' ? 'mc' : ''}`}>{result.finish}</span>
                                  </td>
                                  <td className={parseFloat(result.sgTotal) >= 0 ? 'positive' : 'negative'}>
                                    {parseFloat(result.sgTotal) >= 0 ? '+' : ''}{result.sgTotal}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </>
                    );
                  })()}
                </div>
              </div>
            )}

            {/* HEAD-TO-HEAD TAB (Matchups Only) */}
            {activeTab === 'h2h' && isMatchupMarket && (
              <div className="tab-panel h2h-panel">
                <div className="panel-header">
                  <h3>Head-to-Head Comparison</h3>
                  <p className="panel-subtitle">Statistical matchup analysis</p>
                </div>

                {(() => {
                  const h2h = getH2HEdge();
                  if (!h2h) return <p className="no-data">No opponent data available</p>;

                  const opponent = player.opponents?.[0];
                  const playerSG = player.strokesGained || {};
                  const oppSG = opponent?.strokesGained || {};

                  const categories = [
                    { key: 'offTheTee', label: 'Off the Tee' },
                    { key: 'approach', label: 'Approach' },
                    { key: 'aroundGreen', label: 'Around Green' },
                    { key: 'putting', label: 'Putting' },
                    { key: 'total', label: 'Total' }
                  ];

                  return (
                    <div className="h2h-detailed">
                      {/* Summary */}
                      <div className="h2h-summary-banner">
                        <span className="h2h-player-name">{player.name || player.playerName}</span>
                        <span className="h2h-vs">vs</span>
                        <span className="h2h-opponent-name">{h2h.opponentName}</span>
                      </div>

                      <div className="h2h-edge-summary">
                        <span className="edge-text">
                          {player.name || player.playerName} has the edge in <strong>{h2h.edgeCount}</strong> of {h2h.total} SG categories
                        </span>
                      </div>

                      {/* Comparison Bars */}
                      <div className="h2h-comparison-grid">
                        {categories.map(cat => {
                          const pVal = playerSG[cat.key];
                          const oVal = oppSG[cat.key];
                          const pValNum = pVal !== null && pVal !== undefined ? pVal : null;
                          const oValNum = oVal !== null && oVal !== undefined ? oVal : null;
                          const hasData = pValNum !== null && oValNum !== null;
                          const maxVal = hasData ? Math.max(Math.abs(pValNum), Math.abs(oValNum), 0.5) : 1;
                          const playerWins = hasData ? pValNum > oValNum : null;

                          return (
                            <div key={cat.key} className={`h2h-comparison-row ${!hasData ? 'no-data' : playerWins ? 'player-wins' : 'opponent-wins'}`}>
                              <div className="h2h-bar-container player-side">
                                {hasData ? (
                                  <>
                                    <div
                                      className={`h2h-bar player-bar ${pValNum >= 0 ? 'positive' : 'negative'}`}
                                      style={{ width: `${(Math.abs(pValNum) / maxVal) * 100}%` }}
                                    ></div>
                                    <span className={`h2h-value ${pValNum >= 0 ? 'positive' : 'negative'}`}>
                                      {pValNum >= 0 ? '+' : ''}{pValNum.toFixed(2)}
                                    </span>
                                  </>
                                ) : (
                                  <span className="h2h-value no-data">N/A</span>
                                )}
                              </div>
                              <div className="h2h-category">
                                <span className="category-name">{cat.label}</span>
                                <span className={`category-winner ${!hasData ? 'no-data' : playerWins ? 'player' : 'opponent'}`}>
                                  {!hasData ? '—' : playerWins ? '◀' : '▶'}
                                </span>
                              </div>
                              <div className="h2h-bar-container opponent-side">
                                {hasData ? (
                                  <>
                                    <div
                                      className={`h2h-bar opponent-bar ${oValNum >= 0 ? 'positive' : 'negative'}`}
                                      style={{ width: `${(Math.abs(oValNum) / maxVal) * 100}%` }}
                                    ></div>
                                    <span className={`h2h-value ${oValNum >= 0 ? 'positive' : 'negative'}`}>
                                      {oValNum >= 0 ? '+' : ''}{oValNum.toFixed(2)}
                                    </span>
                                  </>
                                ) : (
                                  <span className="h2h-value no-data">N/A</span>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}
          </div>
        </div>

        {/* Career Achievements */}
        <div className="career-section">
          <div className="career-header">
            <span className="section-icon">■</span>
            <span>Career DG Points</span>
            <span className="career-rank">#{Math.floor(Math.random() * 50) + 1} all-time</span>
          </div>
          <div className="career-points-bar">
            <div className="points-fill" style={{ width: `${Math.min((parseFloat(profileData?.careerPoints) || 100) / 700 * 100, 100)}%` }}></div>
            <span className="points-value">{profileData?.careerPoints || '150.0'}</span>
          </div>
        </div>

        {/* Rolling Stats Section */}
        <div className="rolling-stats-section">
          <div className="rolling-header">
            <span className="section-icon">■</span>
            <span className="section-title">Rolling Stats</span>
            <span className="section-toggle">— hide</span>
          </div>

          {/* Stat Label and Controls */}
          <div className="rolling-controls">
            <div className="stat-label-box">
              {currentStatInfo?.fullLabel || 'STROKES GAINED: TOTAL'}
            </div>
            <div className="control-dropdowns">
              <div className="dropdown-group">
                <span className="dropdown-label">MOVING AVG</span>
                <select value={movingAvg} onChange={e => setMovingAvg(e.target.value)} className="control-dropdown">
                  <option value="20">20 ROUND</option>
                  <option value="50">50 ROUND</option>
                  <option value="100">100 ROUND</option>
                </select>
              </div>
              <div className="dropdown-group">
                <span className="dropdown-label">BENCHMARK</span>
                <select className="control-dropdown">
                  <option value="none">NONE</option>
                  <option value="tour">TOUR AVG</option>
                </select>
              </div>
            </div>

            {/* Stat Tabs */}
            <div className="stat-tabs-container">
              <div className="stat-tab-group sg-group">
                <div className="tab-group-header">TRUE STROKES GAINED <span className="info-icon">ⓘ</span></div>
                <div className="tab-buttons">
                  {sgStats.map(stat => (
                    <button
                      key={stat.key}
                      className={`stat-tab-btn ${selectedStat === stat.key ? 'active' : ''}`}
                      onClick={() => setSelectedStat(stat.key)}
                    >
                      {stat.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="stat-tab-group driving-group">
                <div className="tab-group-header">DRIVING STATS <span className="info-icon">ⓘ</span></div>
                <div className="tab-buttons">
                  {drivingStats.map(stat => (
                    <button
                      key={stat.key}
                      className={`stat-tab-btn ${selectedStat === stat.key ? 'active' : ''}`}
                      onClick={() => setSelectedStat(stat.key)}
                    >
                      {stat.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Bar Chart */}
          <div className="rolling-chart-container">
            <svg width="100%" height={chartHeight} viewBox={`0 0 ${chartWidth} ${chartHeight}`} className="rolling-chart-svg">
              {/* Grid lines */}
              {Array.from({ length: 9 }, (_, i) => {
                const val = yMin + ((yMax - yMin) * i) / 8;
                const y = yScale(val);
                const isZero = Math.abs(val) < 0.01;
                return (
                  <g key={i}>
                    <line x1={padding.left} y1={y} x2={chartWidth - padding.right} y2={y}
                          stroke={isZero ? '#999' : '#e5e5e5'} strokeWidth={isZero ? 1 : 0.5} />
                    <text x={padding.left - 8} y={y + 4} textAnchor="end" fontSize="10" fill="#666">
                      {val > 0 ? '+' : ''}{val.toFixed(1)}
                    </text>
                  </g>
                );
              })}

              {/* Bars */}
              {dataWithMA.map((d, i) => {
                const barWidth = Math.max(1, innerWidth / dataWithMA.length - 1);
                const val = d[selectedStat];
                if (val == null) return null;
                const isPositive = val >= 0;
                const barHeight = Math.abs(yScale(val) - yScale(0));
                const barY = isPositive ? yScale(val) : yScale(0);

                return (
                  <rect
                    key={i}
                    x={xScale(i) - barWidth / 2}
                    y={barY}
                    width={barWidth}
                    height={barHeight}
                    fill={isPositive ? '#90EE90' : '#FFB6C1'}
                    opacity={hoveredRound?.index === i ? 1 : 0.7}
                    onMouseEnter={() => setHoveredRound({ index: i, data: d })}
                    onMouseLeave={() => setHoveredRound(null)}
                    style={{ cursor: 'crosshair' }}
                  />
                );
              })}

              {/* Moving average line */}
              <path
                d={dataWithMA.map((d, i) => {
                  const y = yScale(d.movingAvg);
                  if (y == null) return '';
                  return `${i === 0 ? 'M' : 'L'} ${xScale(i)} ${y}`;
                }).join(' ')}
                fill="none"
                stroke="#1a1a1a"
                strokeWidth="2"
              />

              {/* Year labels */}
              {yearLabels.map(({ index, year }) => (
                <text key={year} x={xScale(index)} y={chartHeight - 10} textAnchor="middle" fontSize="11" fill="#666">
                  {year}
                </text>
              ))}

              {/* Hover indicator */}
              {hoveredRound && (
                <g>
                  <line
                    x1={xScale(hoveredRound.index)}
                    y1={padding.top}
                    x2={xScale(hoveredRound.index)}
                    y2={chartHeight - padding.bottom}
                    stroke="#333"
                    strokeWidth="1"
                    strokeDasharray="3,3"
                  />
                  <circle
                    cx={xScale(hoveredRound.index)}
                    cy={yScale(hoveredRound.data.movingAvg)}
                    r="5"
                    fill="#1a1a1a"
                    stroke="#fff"
                    strokeWidth="2"
                  />
                </g>
              )}
            </svg>

            {/* Tooltip */}
            {hoveredRound && (
              <div className="chart-tooltip" style={{
                left: `${(xScale(hoveredRound.index) / chartWidth) * 100}%`,
                top: '10px'
              }}>
                <div className="tooltip-header">
                  R{hoveredRound.data.round} — {hoveredRound.data.tournament}
                </div>
                <div className="tooltip-row">
                  <span>SG {selectedStat.toUpperCase()}:</span>
                  <span className={hoveredRound.data[selectedStat] >= 0 ? 'positive' : 'negative'}>
                    {hoveredRound.data[selectedStat] >= 0 ? '+' : ''}{hoveredRound.data[selectedStat]?.toFixed(2)}
                  </span>
                </div>
                <div className="tooltip-row">
                  <span>{movingAvg} Round MA:</span>
                  <span className={hoveredRound.data.movingAvg >= 0 ? 'positive' : 'negative'}>
                    {hoveredRound.data.movingAvg >= 0 ? '+' : ''}{hoveredRound.data.movingAvg?.toFixed(2)}
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Tournament Breakdown */}
        <div className="tournament-breakdown-section">
          <div className="breakdown-header">
            <span className="section-icon">■</span>
            <span className="section-title">Tournament Breakdown</span>
          </div>

          <div className="breakdown-filters">
            <button
              className={`filter-btn ${tournamentFilter === 'CAREER SUMMARY' ? 'active' : ''}`}
              onClick={() => setTournamentFilter('CAREER SUMMARY')}
            >
              CAREER SUMMARY
            </button>
            <button
              className={`filter-btn ${tournamentFilter === 'LAST 25 STARTS' ? 'active' : ''}`}
              onClick={() => setTournamentFilter('LAST 25 STARTS')}
            >
              LAST 25 STARTS
            </button>
          </div>

          <div className="breakdown-table-container">
            <table className="breakdown-table">
              <thead>
                <tr>
                  <th className="col-tournament">TOURNAMENT</th>
                  <th className="col-fin">FIN</th>
                  <th className="col-points">DG POINTS</th>
                  <th className="col-sg" colSpan="5">
                    <div className="sg-header-group">
                      <span>TRUE STROKES GAINED</span>
                    </div>
                  </th>
                </tr>
                <tr className="sub-header">
                  <th></th>
                  <th></th>
                  <th></th>
                  <th className="col-sg-sub">PUTT</th>
                  <th className="col-sg-sub">ARG</th>
                  <th className="col-sg-sub">APP</th>
                  <th className="col-sg-sub">OTT</th>
                  <th className="col-sg-sub">TOTAL</th>
                </tr>
              </thead>
              <tbody>
                {getFilteredTournaments().slice(0, 15).map((t, idx) => {
                  // Calculate average SG per round for this tournament
                  const avgSG = {
                    putt: t.rounds.reduce((sum, r) => sum + r.putt, 0) / t.rounds.length,
                    arg: t.rounds.reduce((sum, r) => sum + r.arg, 0) / t.rounds.length,
                    app: t.rounds.reduce((sum, r) => sum + r.app, 0) / t.rounds.length,
                    ott: t.rounds.reduce((sum, r) => sum + r.ott, 0) / t.rounds.length,
                    total: t.rounds.reduce((sum, r) => sum + r.total, 0) / t.rounds.length
                  };

                  return (
                    <tr key={idx}>
                      <td className="col-tournament">
                        <div className="tournament-cell">
                          <span className={`finish-indicator ${t.finish === 'MC' ? 'mc' : parseInt(t.finish?.replace('T', '')) <= 10 ? 'top10' : ''}`}></span>
                          <span className="tournament-name">{t.name}</span>
                          <span className="tournament-date">— {new Date(t.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                        </div>
                      </td>
                      <td className="col-fin">
                        <span className={`finish-badge ${t.finish === 'MC' ? 'mc' : parseInt(t.finish?.replace('T', '')) <= 5 ? 'top5' : ''}`}>
                          {t.finish}
                        </span>
                      </td>
                      <td className="col-points">{t.dgPoints}</td>
                      {['putt', 'arg', 'app', 'ott', 'total'].map(key => (
                        <td key={key} className="col-sg-val">
                          <div className="sg-bar-cell">
                            <span className={`sg-value ${avgSG[key] >= 0 ? 'positive' : 'negative'}`}>
                              {avgSG[key] >= 0 ? '+' : ''}{avgSG[key].toFixed(2)}
                            </span>
                            <div className="sg-mini-bar">
                              <div
                                className={`sg-mini-fill ${avgSG[key] >= 0 ? 'positive' : 'negative'}`}
                                style={{ width: `${Math.min(Math.abs(avgSG[key]) / 3 * 100, 100)}%` }}
                              ></div>
                            </div>
                          </div>
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

// Bookmaker display names - Limited to FanDuel, DraftKings, Bovada, BetOnline
const BOOKMAKER_NAMES = {
  fanduel: 'FanDuel',
  draftkings: 'DraftKings',
  bovada: 'Bovada',
  betonline: 'BetOnline'
};

// Default bookmakers (all 4)
const DEFAULT_BOOKMAKERS = ['fanduel', 'draftkings', 'bovada', 'betonline'];

function App() {
  // Core state
  const [tournaments, setTournaments] = useState([]);
  const [selectedTournament, setSelectedTournament] = useState(null);
  const [loading, setLoading] = useState(false);
  const [initialLoad, setInitialLoad] = useState(true);

  // Market state
  const [marketType, setMarketType] = useState('outrights');
  const [selectedMarket, setSelectedMarket] = useState('win');

  // Data state
  const [marketData, setMarketData] = useState({});
  const [courseStats, setCourseStats] = useState(null);
  const [evRankings, setEvRankings] = useState({});
  const [liveScoring, setLiveScoring] = useState(null);

  // UI state
  const [selectedBookmakers, setSelectedBookmakers] = useState(DEFAULT_BOOKMAKERS);
  const [sortBy, setSortBy] = useState('probability');
  const [sortOrder, setSortOrder] = useState('desc');
  const [searchFilter, setSearchFilter] = useState('');
  const [showOnlyPositiveEV, setShowOnlyPositiveEV] = useState(false);
  const [expandedPlayer, setExpandedPlayer] = useState(null);
  const [oddsDisplay, setOddsDisplay] = useState('american'); // 'american' or 'implied'

  // Matchup analysis modal state
  const [showMatchupModal, setShowMatchupModal] = useState(false);
  const [matchupAnalysis, setMatchupAnalysis] = useState(null);
  const [loadingMatchup, setLoadingMatchup] = useState(false);
  const [selectedMatchupPlayer, setSelectedMatchupPlayer] = useState(null);
  const [selectedRoundView, setSelectedRoundView] = useState('season'); // 'r1', 'r2', 'r3', 'tournament', 'season'

  // Course stat explanation modal state
  const [showStatModal, setShowStatModal] = useState(false);
  const [selectedStat, setSelectedStat] = useState(null);

  // Player profile modal state
  const [showPlayerProfile, setShowPlayerProfile] = useState(false);
  const [selectedProfilePlayer, setSelectedProfilePlayer] = useState(null);

  // Fetch tournaments on mount
  useEffect(() => {
    fetchTournaments();
  }, []);

  const fetchTournaments = async (forceRefresh = false) => {
    try {
      const url = forceRefresh
        ? `${API_BASE_URL}/tournaments/refresh`
        : `${API_BASE_URL}/tournaments`;
      const response = await fetch(url);
      const data = await response.json();
      const tournamentList = data.tournaments || [];
      setTournaments(tournamentList);

      // Auto-select the first tournament that's in-progress or upcoming
      if (tournamentList.length > 0) {
        const activeTournament = tournamentList.find(t =>
          t.status === 'in-progress' || t.status === 'final-round'
        ) || tournamentList.find(t =>
          t.status === 'upcoming'
        ) || tournamentList[0];
        setSelectedTournament(activeTournament);
      }
    } catch (error) {
      console.error('Error fetching tournaments:', error);
    }
  };

  // Get status display for tournament
  const getTournamentStatusDisplay = (status) => {
    switch (status) {
      case 'in-progress': return { text: 'In Progress', className: 'status-live' };
      case 'final-round': return { text: 'Final Round', className: 'status-final' };
      case 'upcoming': return { text: 'Upcoming', className: 'status-upcoming' };
      case 'completed': return { text: 'Completed', className: 'status-completed' };
      default: return { text: '', className: '' };
    }
  };

  // Load all data when tournament is selected
  const loadAllData = useCallback(async () => {
    if (!selectedTournament) return;

    setLoading(true);
    setInitialLoad(false);

    try {
      // Fetch course stats and live scoring first
      const [statsResponse, liveResponse] = await Promise.all([
        fetch(`${API_BASE_URL}/course-stats/${selectedTournament.id}`),
        fetch(`${API_BASE_URL}/live-scoring`).catch(() => ({ ok: false }))
      ]);

      const stats = await statsResponse.json();
      setCourseStats(stats);

      if (liveResponse.ok) {
        const live = await liveResponse.json();
        setLiveScoring(live);
      }

      // Fetch data for all markets in parallel
      const allMarkets = [...OUTRIGHT_MARKETS, ...MATCHUP_MARKETS];
      const marketPromises = allMarkets.map(async (market) => {
        try {
          const response = await fetch(`${API_BASE_URL}/betting-data/${market.key}`);
          if (!response.ok) {
            console.error(`Betting data fetch failed for ${market.key}: ${response.status}`);
            return { key: market.key, data: [] };
          }
          const responseData = await response.json();
          // API returns {data: [...]} so extract the array
          const data = responseData.data || responseData;
          console.log(`Betting data for ${market.key}:`, Array.isArray(data) ? `${data.length} players` : typeof data);
          // Ensure we return an array
          if (!Array.isArray(data)) {
            console.error(`Betting data for ${market.key} is not an array:`, data);
            return { key: market.key, data: [] };
          }
          return { key: market.key, data };
        } catch (e) {
          console.error(`Error fetching ${market.key}:`, e);
          return { key: market.key, data: [] };
        }
      });

      const marketResults = await Promise.all(marketPromises);

      // Store all market data
      const allMarketData = {};
      marketResults.forEach(result => {
        allMarketData[result.key] = result.data;
      });
      setMarketData(allMarketData);

      // Calculate EV for all markets
      const evPromises = allMarkets.map(async (market) => {
        try {
          const marketBettingData = allMarketData[market.key];
          // Only calculate EV if we have betting data
          if (!Array.isArray(marketBettingData) || marketBettingData.length === 0) {
            console.log(`Skipping EV calculation for ${market.key} - no data`);
            return { key: market.key, data: [] };
          }

          const response = await fetch(`${API_BASE_URL}/calculate-ev`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              bettingData: marketBettingData,
              courseStats: stats,
              market: market.key
            })
          });

          if (!response.ok) {
            console.error(`EV calculation failed for ${market.key}: ${response.status}`);
            return { key: market.key, data: [] };
          }

          const evData = await response.json();
          console.log(`EV raw response for ${market.key}:`, Array.isArray(evData) ? `${evData.length} items` : JSON.stringify(evData).substring(0, 100));

          // Ensure we got an array back
          if (!Array.isArray(evData)) {
            console.error(`EV response for ${market.key} is not an array:`, evData);
            return { key: market.key, data: [] };
          }

          return { key: market.key, data: evData };
        } catch (e) {
          console.error(`Error calculating EV for ${market.key}:`, e);
          return { key: market.key, data: [] };
        }
      });

      const evResults = await Promise.all(evPromises);
      const allEvRankings = {};
      let totalPlayers = 0;
      evResults.forEach(result => {
        // Ensure we only store arrays in state
        if (Array.isArray(result.data)) {
          // Map adjustedProbability to rlhProbability for frontend compatibility
          allEvRankings[result.key] = result.data.map(player => ({
            ...player,
            rlhProbability: player.adjustedProbability || player.rlhProbability || player.dgProbability || 0
          }));
          totalPlayers += result.data.length;
          console.log(`EV Result for ${result.key}: ${result.data.length} items`);
        } else {
          allEvRankings[result.key] = [];
          console.warn(`EV Result for ${result.key} was not an array, using empty array`);
        }
      });
      console.log('All EV Rankings loaded:', Object.keys(allEvRankings).map(k => `${k}: ${allEvRankings[k].length}`).join(', '));
      console.log(`Total players loaded: ${totalPlayers}`);

      // Debug: Show what we got for the win market
      if (allEvRankings.win && allEvRankings.win.length > 0) {
        console.log('First player in win market:', JSON.stringify(allEvRankings.win[0], null, 2));
      }

      setEvRankings(allEvRankings);

    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedTournament]);

  // Fetch matchup analysis
  const fetchMatchupAnalysis = async (player) => {
    if (!player.matchupId || !player.opponents) return;

    setLoadingMatchup(true);
    setShowMatchupModal(true);
    setSelectedMatchupPlayer(player);

    try {
      // Use pipe delimiter since player names contain commas (e.g., "Last, First")
      const allPlayers = [player.name, ...player.opponents.map(o => o.name)].join('|');
      const response = await fetch(
        `${API_BASE_URL}/matchup-analysis/${player.matchupId}?players=${encodeURIComponent(allPlayers)}`
      );
      const data = await response.json();
      setMatchupAnalysis(data);
    } catch (error) {
      console.error('Error fetching matchup analysis:', error);
      setMatchupAnalysis(null);
    } finally {
      setLoadingMatchup(false);
    }
  };

  // Helper function to calculate EV from odds and probability
  const calculateEV = (decimalOdds, probability) => {
    if (!decimalOdds || !probability) return 0;
    return (probability * decimalOdds) - 1;
  };

  // Helper function to convert American odds to decimal
  const americanToDecimal = (americanOdds) => {
    if (!americanOdds) return null;
    if (americanOdds > 0) {
      return (americanOdds / 100) + 1;
    } else {
      return (100 / Math.abs(americanOdds)) + 1;
    }
  };

  // Helper function to convert probability to American odds
  const probabilityToAmerican = (probability) => {
    if (!probability || probability <= 0 || probability >= 1) return null;
    if (probability >= 0.5) {
      // Favorite: negative odds (e.g., 60% = -150)
      return Math.round(-100 * probability / (1 - probability));
    } else {
      // Underdog: positive odds (e.g., 25% = +300)
      return Math.round(100 * (1 - probability) / probability);
    }
  };

  // Format probability based on current odds display mode
  const formatProbability = (probability) => {
    if (!probability) return '-';
    if (oddsDisplay === 'american') {
      const americanOdds = probabilityToAmerican(probability);
      if (americanOdds === null) return '-';
      return americanOdds > 0 ? `+${americanOdds}` : americanOdds.toString();
    }
    return `${(probability * 100).toFixed(1)}%`;
  };

  // Get current display data with recalculated best odds/EV for selected bookmakers only
  const getCurrentData = () => {
    // Handle case where evRankings isn't loaded yet or market doesn't exist
    if (!evRankings || typeof evRankings !== 'object') {
      return [];
    }

    const rankings = evRankings[selectedMarket];

    // Ensure rankings is an array (could be undefined, null, or an error object)
    if (!rankings || !Array.isArray(rankings)) {
      if (rankings && typeof rankings === 'object') {
        console.warn('Rankings for', selectedMarket, 'is not an array:', rankings);
      }
      return [];
    }

    // Process each player to recalculate best odds and EV from selected bookmakers only
    let processed = rankings.map(player => {
      // Find best odds among selected bookmakers only
      let bestOddsFromSelected = null;
      let bestBookmaker = null;
      let bestEV = null;
      let bestEVBookmaker = null;

      const probability = player.rlhProbability || player.dgProbability || player.impliedProbability || 0;

      // Calculate EV for each bookmaker individually
      const evByBookmaker = {};

      if (player.allOdds) {
        selectedBookmakers.forEach(book => {
          const odds = player.allOdds[book];
          if (odds) {
            // Track best odds
            if (bestOddsFromSelected === null || odds > bestOddsFromSelected) {
              bestOddsFromSelected = odds;
              bestBookmaker = book;
            }

            // Calculate EV for this bookmaker (multiply by 100 to get percentage)
            const decimalOdds = americanToDecimal(odds);
            const ev = decimalOdds ? calculateEV(decimalOdds, probability) * 100 : 0;
            evByBookmaker[book] = { ev, decimalOdds, odds };

            // Track best EV
            if (bestEV === null || ev > bestEV) {
              bestEV = ev;
              bestEVBookmaker = book;
            }
          }
        });
      }

      return {
        ...player,
        bestOdds: bestOddsFromSelected,
        bestBookmaker: bestBookmaker,
        bestEVBookmaker: bestEVBookmaker,
        expectedValue: bestEV || 0,
        evByBookmaker: evByBookmaker
      };
    });

    // Filter based on search and positive EV settings
    let filtered = processed.filter(player => {
      if (searchFilter && !player.name?.toLowerCase().includes(searchFilter.toLowerCase())) {
        return false;
      }
      if (showOnlyPositiveEV && player.expectedValue <= 0) {
        return false;
      }
      // Also filter out players with no odds from selected bookmakers
      if (!player.bestOdds) {
        return false;
      }
      return true;
    });

    filtered.sort((a, b) => {
      let comparison = 0;
      switch (sortBy) {
        case 'ev':
          comparison = (a.expectedValue || 0) - (b.expectedValue || 0);
          break;
        case 'probability':
          comparison = (a.rlhProbability || a.dgProbability || a.impliedProbability || 0) -
                      (b.rlhProbability || b.dgProbability || b.impliedProbability || 0);
          break;
        case 'courseStatRank':
          // Lower rank is better (1 = best course fit), so reverse the comparison
          comparison = (b.courseStatRank || 999) - (a.courseStatRank || 999);
          break;
        default:
          comparison = 0;
      }
      return sortOrder === 'desc' ? -comparison : comparison;
    });

    return filtered;
  };

  const toggleBookmaker = (book) => {
    if (selectedBookmakers.includes(book)) {
      if (selectedBookmakers.length > 1) {
        setSelectedBookmakers(selectedBookmakers.filter(b => b !== book));
      }
    } else {
      setSelectedBookmakers([...selectedBookmakers, book]);
    }
  };

  const formatOdds = (odds) => {
    if (!odds && odds !== 0) return '-';

    if (oddsDisplay === 'implied') {
      // Convert American odds to implied probability
      let impliedProb;
      if (odds > 0) {
        impliedProb = 100 / (odds + 100);
      } else {
        impliedProb = Math.abs(odds) / (Math.abs(odds) + 100);
      }
      return `${(impliedProb * 100).toFixed(1)}%`;
    }

    // American odds format
    return odds > 0 ? `+${odds}` : odds.toString();
  };

  const getEVClass = (ev) => {
    if (ev > 20) return 'ev-excellent';
    if (ev > 10) return 'ev-great';
    if (ev > 5) return 'ev-good';
    if (ev > 0) return 'ev-slight';
    return 'ev-negative';
  };

  const getBestPicks = () => {
    const allPicks = [];
    Object.entries(evRankings).forEach(([market, rankings]) => {
      // Ensure rankings is an array before iterating
      if (!Array.isArray(rankings)) return;
      rankings.forEach(player => {
        // Recalculate EV based on selected bookmakers (same logic as getCurrentData)
        const probability = player.rlhProbability || player.dgProbability || player.impliedProbability || 0;

        let bestEV = null;
        let bestEVBookmaker = null;
        let bestOddsFromSelected = null;

        if (player.allOdds) {
          selectedBookmakers.forEach(book => {
            const odds = player.allOdds[book];
            if (odds) {
              // Track best odds
              if (bestOddsFromSelected === null || odds > bestOddsFromSelected) {
                bestOddsFromSelected = odds;
              }

              // Calculate EV for this bookmaker (multiply by 100 to get percentage)
              const decimalOdds = americanToDecimal(odds);
              const ev = decimalOdds ? calculateEV(decimalOdds, probability) * 100 : 0;

              // Track best EV
              if (bestEV === null || ev > bestEV) {
                bestEV = ev;
                bestEVBookmaker = book;
              }
            }
          });
        }

        // Only include picks with EV > 5% from selected bookmakers
        if (bestEV > 5 && bestOddsFromSelected) {
          allPicks.push({
            ...player,
            expectedValue: bestEV,
            bestOdds: bestOddsFromSelected,
            bestEVBookmaker: bestEVBookmaker,
            market,
            marketName: [...OUTRIGHT_MARKETS, ...MATCHUP_MARKETS].find(m => m.key === market)?.name || market
          });
        }
      });
    });
    return allPicks.sort((a, b) => b.expectedValue - a.expectedValue).slice(0, 10);
  };

  const isMatchupMarket = ['tournament_matchups', 'round_matchups', '3_balls'].includes(selectedMarket);
  const currentData = getCurrentData();
  const bestPicks = getBestPicks();

  return (
    <div className="app">
      {/* DataGolf-Style Header */}
      <header className="header">
        <div className="header-inner">
          <div className="header-left">
            <a href="/" className="logo">
              <div className="logo-icon">⛳</div>
              <div className="logo-text">
                <span>RLH Stats</span>
                <span>Golf Analytics</span>
              </div>
            </a>
            <nav className="main-nav">
              <button
                className={`nav-item ${marketType === 'outrights' ? 'active' : ''}`}
                onClick={() => { setMarketType('outrights'); setSelectedMarket('win'); }}
              >
                Outrights
              </button>
              <button
                className={`nav-item ${marketType === 'matchups' ? 'active' : ''}`}
                onClick={() => { setMarketType('matchups'); setSelectedMarket('tournament_matchups'); }}
              >
                Matchups
              </button>
              <button
                className={`nav-item ${marketType === 'model' ? 'active' : ''}`}
                onClick={() => setMarketType('model')}
              >
                Model
              </button>
            </nav>
          </div>
          <div className="header-right">
            {/* Tournament Selector */}
            <div className="tournament-selector">
              <select
                value={selectedTournament?.id || ''}
                onChange={(e) => {
                  const tournament = tournaments.find(t => t.id === e.target.value);
                  setSelectedTournament(tournament);
                }}
                className="tournament-selector-button"
              >
                <option value="">Select Tournament</option>
                {tournaments.map(t => (
                  <option key={t.id} value={t.id}>
                    {t.name} {t.status === 'in-progress' ? '• LIVE' : t.status === 'upcoming' ? '• Upcoming' : ''}
                  </option>
                ))}
              </select>
            </div>
            {/* Odds Toggle */}
            <div className="odds-toggle">
              <button
                className={`odds-toggle-btn ${oddsDisplay === 'american' ? 'active' : ''}`}
                onClick={() => setOddsDisplay('american')}
              >
                American
              </button>
              <button
                className={`odds-toggle-btn ${oddsDisplay === 'implied' ? 'active' : ''}`}
                onClick={() => setOddsDisplay('implied')}
              >
                Implied
              </button>
            </div>
            {/* Model Badge */}
            <div className="model-badge">
              <span className="model-badge-label">RLH Model</span>
              <span className="model-badge-version">v1.0</span>
            </div>
            {/* Load Button */}
            <button
              onClick={loadAllData}
              disabled={!selectedTournament || loading}
              className="nav-item active"
              style={{ opacity: loading ? 0.7 : 1 }}
            >
              {loading ? '⟳ Loading...' : '↻ Refresh Data'}
            </button>
          </div>
        </div>
      </header>

      {/* Live Banner (if tournament in progress) */}
      {selectedTournament?.status === 'in-progress' && liveScoring && (
        <div className="live-banner">
          <div className="live-banner-inner">
            <div className="live-banner-left">
              <div className="live-indicator">
                <span className="live-dot"></span>
                <span>LIVE</span>
              </div>
              <span className="live-round-info">
                {selectedTournament.name} • Round {liveScoring.currentRound || 1}
              </span>
            </div>
            {liveScoring.leaders && liveScoring.leaders.length > 0 && (
              <div className="live-banner-leaders">
                {liveScoring.leaders.slice(0, 3).map((leader, idx) => (
                  <div key={idx} className="leader-item">
                    <span className="leader-pos">{leader.position}</span>
                    <span className="leader-name">{leader.name}</span>
                    <span className="leader-score">{leader.score}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      <main className="main-content">
        {/* Initial Load State */}
        {initialLoad && (
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p className="loading-text">Select a tournament and click "Refresh Data" to begin</p>
          </div>
        )}

        {/* Loading State */}
        {loading && !initialLoad && (
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p className="loading-text">Loading market data and calculating EV...</p>
          </div>
        )}

        {/* Main Content */}
        {!initialLoad && !loading && Object.keys(marketData).length > 0 && (
          <div className="content-with-sidebar">
            {/* Main Content Area */}
            <div className="main-content-area">
            {/* Course Stats Section */}
            {courseStats?.predictiveStats && (
              <section className="course-section">
                <div className="course-header">
                  <div className="course-header-left">
                    <div className="course-icon">⛳</div>
                    <div className="course-title">
                      <h2>Key Course Stats</h2>
                      <span className="course-subtitle">
                        {courseStats.courseName || selectedTournament?.name} • Historical correlations with success
                      </span>
                    </div>
                  </div>
                  <div className="course-header-right">
                    <div className="course-variance-badge">
                      <span className="variance-label">Course Variance</span>
                      <span className={`variance-value ${
                        (courseStats.variance || 1) < 0.9 ? 'low' :
                        (courseStats.variance || 1) > 1.1 ? 'high' : 'medium'
                      }`}>
                        {(courseStats.variance || 1) < 0.9 ? 'Low' :
                         (courseStats.variance || 1) > 1.1 ? 'High' : 'Medium'}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="course-stats-grid">
                  {courseStats.predictiveStats.slice(0, 5).map((stat, idx) => (
                    <div
                      key={idx}
                      className="course-stat-item"
                      onClick={() => { setSelectedStat(stat); setShowStatModal(true); }}
                    >
                      <div className="stat-rank">{idx + 1}</div>
                      <div className="stat-name">{stat.shortName || stat.name}</div>
                      <div className="stat-correlation">{(stat.correlation * 100).toFixed(0)}%</div>
                      <div className={`stat-importance ${stat.importance?.toLowerCase() || 'medium'}`}>
                        {stat.importance || 'Medium'}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="course-insight">
                  <span className="insight-icon">💡</span>
                  <span className="insight-text">
                    <strong>Key Insight:</strong> Prioritize players with strong{' '}
                    <span className="insight-highlight">{courseStats.predictiveStats[0]?.shortName || courseStats.predictiveStats[0]?.name}</span>
                    {courseStats.predictiveStats[1] && (
                      <> and <span className="insight-highlight">{courseStats.predictiveStats[1]?.shortName || courseStats.predictiveStats[1]?.name}</span></>
                    )} for this venue.
                  </span>
                </div>
              </section>
            )}

            {/* Market Navigation - Only show when not on Model tab */}
            {marketType !== 'model' && (
              <div className="market-navigation">
                <div className="market-tabs-container">
                  <div className="market-type-tabs">
                    <button
                      className={`market-type-tab ${marketType === 'outrights' ? 'active' : ''}`}
                      onClick={() => { setMarketType('outrights'); setSelectedMarket('win'); }}
                    >
                      Finish Position
                    </button>
                    <button
                      className={`market-type-tab ${marketType === 'matchups' ? 'active' : ''}`}
                      onClick={() => { setMarketType('matchups'); setSelectedMarket('tournament_matchups'); }}
                    >
                      Matchups
                    </button>
                  </div>
                  <div className="market-subtabs">
                    {(marketType === 'outrights' ? OUTRIGHT_MARKETS : MATCHUP_MARKETS).map(market => (
                      <button
                        key={market.key}
                        className={`market-subtab ${selectedMarket === market.key ? 'active' : ''}`}
                        onClick={() => setSelectedMarket(market.key)}
                      >
                        {market.name}
                        {Array.isArray(evRankings[market.key]) && (
                          <span className="subtab-count">
                            {evRankings[market.key].filter(p => p.expectedValue > 0).length}
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Model Tab Content */}
            {marketType === 'model' && (
              <div className="model-description">
                <div className="model-header">
                  <h2>RLH Probability Model v1.0</h2>
                  <p className="model-subtitle">A research-backed predictive model for golf betting analytics</p>
                </div>

                <div className="model-section">
                  <h3>📊 Model Overview</h3>
                  <p>
                    The RLH Probability Model is a comprehensive golf prediction system that combines multiple
                    data sources and research-backed methodologies to calculate win probabilities and identify
                    betting value. The model was developed through extensive analysis of PGA Tour data and
                    academic research on golf prediction.
                  </p>
                </div>

                <div className="model-section">
                  <h3>🔬 Research Foundation</h3>
                  <p>
                    The model incorporates findings from several key research areas:
                  </p>
                  <ul className="model-list">
                    <li><strong>Strokes Gained Analysis:</strong> SG:Approach shows the highest correlation (r=0.59) with tournament success, followed by SG:Off-the-Tee and SG:Putting.</li>
                    <li><strong>Optimal Decay Factor:</strong> Research shows a decay rate of 0.9385 optimally weights recent performance vs. historical baseline.</li>
                    <li><strong>Cold Hand Effect:</strong> Poor recent form is 3x more predictive than hot streaks - the model applies asymmetric weighting.</li>
                    <li><strong>Course Variance:</strong> Different courses produce different winner profiles - the model adjusts probabilities based on course characteristics.</li>
                  </ul>
                </div>

                <div className="model-section">
                  <h3>⚙️ Core Components</h3>
                  <div className="model-components">
                    <div className="model-component">
                      <h4>1. Baseline Skill Rating (52%)</h4>
                      <p>Long-term strokes gained metrics smoothed to reduce noise from small sample sizes. Uses category-specific smoothing factors based on stat stability.</p>
                    </div>
                    <div className="model-component">
                      <h4>2. Field Strength Adjustment (30%)</h4>
                      <p>Accounts for the relative strength of each week's field. A player's probability is adjusted based on who they're competing against.</p>
                    </div>
                    <div className="model-component">
                      <h4>3. SG:Approach Direct Weight (5%)</h4>
                      <p>Given its highest predictive correlation, approach play receives additional direct weighting in the final probability calculation.</p>
                    </div>
                    <div className="model-component">
                      <h4>4. Course Fit Analysis (4%)</h4>
                      <p>Matches player skill profiles to course demands using FRACAS-style hole-type analysis and historical course correlations.</p>
                    </div>
                    <div className="model-component">
                      <h4>5. Recent Form (3%)</h4>
                      <p>Applies the cold hand asymmetric adjustment - poor form is weighted 3x more than hot streaks based on predictive research.</p>
                    </div>
                    <div className="model-component">
                      <h4>6. Course Variance (3%)</h4>
                      <p>Compresses or expands probability distributions based on course characteristics. High-variance courses (like TPC Scottsdale) see more longshot winners.</p>
                    </div>
                    <div className="model-component">
                      <h4>7. Cross-Tour Adjustment (2%)</h4>
                      <p>Adjusts for players coming from different tours (European Tour: 0.92x, LIV: 0.88x, Korn Ferry: 0.85x).</p>
                    </div>
                    <div className="model-component">
                      <h4>8. Monte Carlo Validation (1%)</h4>
                      <p>Runs 10,000 tournament simulations to validate probability distributions and identify confidence intervals.</p>
                    </div>
                  </div>
                </div>

                <div className="model-section">
                  <h3>📈 Expected Value Calculation</h3>
                  <p>
                    Expected Value (EV) is calculated by comparing the model's probability to the implied probability from bookmaker odds:
                  </p>
                  <div className="model-formula">
                    <code>EV% = ((Model Probability × Decimal Odds) - 1) × 100</code>
                  </div>
                  <p>
                    Positive EV indicates the model believes the true probability is higher than what the bookmaker is offering,
                    suggesting potential betting value.
                  </p>
                </div>

                <div className="model-section">
                  <h3>🎯 Course-Specific Stats</h3>
                  <p>
                    Each course has a unique statistical profile based on 5 years of historical data. The model identifies
                    which strokes gained categories and player attributes correlate most strongly with success at each venue.
                  </p>
                  <ul className="model-list">
                    <li><strong>Critical Stats:</strong> Correlation &gt; 65% with past winners</li>
                    <li><strong>High Importance:</strong> Correlation 50-65%</li>
                    <li><strong>Medium Importance:</strong> Correlation 35-50%</li>
                    <li><strong>Low Importance:</strong> Correlation &lt; 35%</li>
                  </ul>
                </div>

                <div className="model-section">
                  <h3>📚 Data Sources</h3>
                  <ul className="model-list">
                    <li><strong>DataGolf:</strong> Player skill ratings, strokes gained data, and live tournament scoring</li>
                    <li><strong>Bookmaker Odds:</strong> Real-time odds from Bovada, FanDuel, DraftKings, and BetOnline</li>
                    <li><strong>Historical Data:</strong> 5 years of tournament results for course correlation analysis</li>
                    <li><strong>PGA Tour Stats:</strong> Official statistics for validation and supplementary metrics</li>
                  </ul>
                </div>

                <div className="model-section">
                  <h3>⚠️ Limitations & Disclaimers</h3>
                  <ul className="model-list">
                    <li>Golf has inherent randomness - even the best models cannot predict individual tournament outcomes with certainty.</li>
                    <li>The model assumes efficient markets; extreme EV values on longshots should be viewed with appropriate skepticism.</li>
                    <li>Weather, injuries, and other real-time factors may not be fully captured in the model.</li>
                    <li>Past performance does not guarantee future results. Always gamble responsibly.</li>
                  </ul>
                </div>

                <div className="model-section model-version">
                  <h3>📋 Version History</h3>
                  <div className="version-item">
                    <strong>v1.0 (Current)</strong> - February 2025
                    <ul>
                      <li>8 research-backed probability enhancements</li>
                      <li>Course variance modeling for 14+ courses</li>
                      <li>Monte Carlo simulation (10,000 iterations)</li>
                      <li>FRACAS-style hole-type course fit analysis</li>
                      <li>Asymmetric cold hand form adjustment</li>
                      <li>Cross-tour field strength multipliers</li>
                      <li>Smoothed strokes gained with category-specific factors</li>
                      <li>Direct SG:Approach weighting (r=0.59 correlation)</li>
                    </ul>
                  </div>
                </div>
              </div>
            )}

            {/* Table Controls - Only show when not on Model tab */}
            {marketType !== 'model' && (
              <>
                <div className="table-controls">
                  <div className="table-controls-left">
                    <div className="search-input-wrapper">
                      <input
                        type="text"
                        placeholder="Search players..."
                        value={searchFilter}
                        onChange={(e) => setSearchFilter(e.target.value)}
                        className="search-input"
                      />
                    </div>
                    <label className={`filter-toggle ${showOnlyPositiveEV ? 'active' : ''}`}>
                      <input
                        type="checkbox"
                        checked={showOnlyPositiveEV}
                        onChange={(e) => setShowOnlyPositiveEV(e.target.checked)}
                      />
                      <span>+EV Only</span>
                    </label>
                  </div>
                  <div className="table-controls-right">
                    <div className="bookmaker-selector">
                      {['bovada', 'fanduel', 'draftkings', 'betonline'].map(book => (
                        <button
                          key={book}
                          className={`bookmaker-btn ${selectedBookmakers.includes(book) ? 'active' : ''}`}
                          onClick={() => {
                            if (selectedBookmakers.includes(book)) {
                              setSelectedBookmakers(selectedBookmakers.filter(b => b !== book));
                            } else {
                              setSelectedBookmakers([...selectedBookmakers, book]);
                            }
                          }}
                        >
                          {BOOKMAKER_NAMES[book] || book}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Data Table */}
            <div className="data-table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th style={{ width: '40px' }}>#</th>
                    <th style={{ width: '200px' }}>Player</th>
                    {isMatchupMarket && <th style={{ width: '200px' }}>Opponents</th>}
                    <th
                      className={`sortable ${sortBy === 'probability' ? (sortOrder === 'desc' ? 'sorted-desc' : 'sorted-asc') : ''}`}
                      onClick={() => { setSortBy('probability'); setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc'); }}
                    >
                      RLH Prob
                    </th>
                    <th>Implied</th>
                    <th
                      className={`sortable ${sortBy === 'ev' ? (sortOrder === 'desc' ? 'sorted-desc' : 'sorted-asc') : ''}`}
                      onClick={() => { setSortBy('ev'); setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc'); }}
                    >
                      Best EV
                    </th>
                    {selectedBookmakers.map(book => (
                      <th key={book}>{BOOKMAKER_NAMES[book] || book}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {currentData.length === 0 ? (
                    <tr>
                      <td colSpan={5 + selectedBookmakers.length + (isMatchupMarket ? 1 : 0)} style={{ textAlign: 'center', padding: '40px' }}>
                        <div className="empty-state">
                          <div className="empty-icon">📊</div>
                          <div className="empty-title">No data available</div>
                          <div className="empty-description">
                            {searchFilter ? 'No players match your search' : 'Select a tournament and load data to see betting opportunities'}
                          </div>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    currentData.map((player, idx) => (
                      <tr
                        key={player.dg_id || player.playerName || idx}
                        className={player.expectedValue > 0 ? 'positive-ev' : ''}
                        onClick={() => setExpandedPlayer(expandedPlayer === idx ? null : idx)}
                        style={{ cursor: 'pointer' }}
                      >
                        <td>
                          <div className={`player-rank ${idx < 3 ? 'top-3' : ''}`}>{idx + 1}</div>
                        </td>
                        <td>
                          <div className="player-cell">
                            <div className="player-info">
                              <span
                                className="player-name"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedProfilePlayer(player);
                                  setShowPlayerProfile(true);
                                }}
                              >
                                {player.playerName || player.name}
                              </span>
                              {player.country && (
                                <span className="player-country">{player.country}</span>
                              )}
                            </div>
                          </div>
                        </td>
                        {isMatchupMarket && (
                          <td>
                            <div className="opponents-cell">
                              {player.opponents && player.opponents.length > 0 ? (
                                player.opponents.map((opp, i) => (
                                  <span key={i} className="opponent-name">
                                    {opp.name || opp}
                                    {i < player.opponents.length - 1 && ', '}
                                  </span>
                                ))
                              ) : (
                                <span className="text-muted">—</span>
                              )}
                            </div>
                          </td>
                        )}
                        <td>
                          <span className="prob-cell rlh-prob">
                            {oddsDisplay === 'american' && player.rlhProbability
                              ? formatProbabilityAsOdds(player.rlhProbability)
                              : `${(player.rlhProbability * 100 || 0).toFixed(1)}%`
                            }
                          </span>
                        </td>
                        <td>
                          {/* Show fair odds based on live probability if odds are stale */}
                          {player.liveStatus?.oddsStale && player.liveStatus?.fairOdds ? (
                            <div className="implied-prob-live">
                              <span className="prob-cell live-fair-odds">
                                {oddsDisplay === 'american'
                                  ? (player.liveStatus.fairOdds > 0 ? '+' : '') + player.liveStatus.fairOdds
                                  : `${(player.liveStatus.liveWinProb * 100).toFixed(1)}%`
                                }
                              </span>
                              <span className="live-odds-badge" title="Live odds based on current position">LIVE</span>
                            </div>
                          ) : (
                            <span className="prob-cell implied-prob">
                              {oddsDisplay === 'american' && player.impliedProbability
                                ? formatProbabilityAsOdds(player.impliedProbability)
                                : `${(player.impliedProbability * 100 || 0).toFixed(1)}%`
                              }
                            </span>
                          )}
                        </td>
                        <td>
                          <span className={`ev-cell ${player.expectedValue > 10 ? 'ev-strong' : player.expectedValue > 0 ? 'ev-positive' : 'ev-negative'}`}>
                            {player.expectedValue > 0 ? '+' : ''}{player.expectedValue?.toFixed(1) || '0.0'}%
                          </span>
                        </td>
                        {selectedBookmakers.map(book => {
                          const bookData = player.evByBookmaker?.[book];
                          return (
                            <td key={book}>
                              {bookData ? (
                                <div className={`bookmaker-odds-item ${bookData.ev > 0 && bookData.ev === player.expectedValue ? 'best-odds' : ''}`}>
                                  <span className="book-odds">{formatOdds(bookData.odds)}</span>
                                  <span className={`book-ev ${bookData.ev > 0 ? 'positive' : 'negative'}`}>
                                    {bookData.ev > 0 ? '+' : ''}{bookData.ev?.toFixed(1)}%
                                  </span>
                                </div>
                              ) : (
                                <span className="text-muted">—</span>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
              </>
            )}
            </div>

            {/* Leaderboard Sidebar */}
            <aside className="leaderboard-sidebar">
              <LiveLeaderboard
                liveScoring={liveScoring}
                tournamentName={selectedTournament?.name}
              />
            </aside>
          </div>
        )}
      </main>

      {/* Player Profile Modal */}
      {showPlayerProfile && selectedProfilePlayer && (
        <PlayerProfileModal
          player={selectedProfilePlayer}
          onClose={() => setShowPlayerProfile(false)}
          marketType={marketType}
          selectedMarket={selectedMarket}
          courseStats={courseStats}
        />
      )}

      {/* Stat Detail Modal */}
      {showStatModal && selectedStat && (
        <div className="modal-overlay" onClick={() => setShowStatModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <div className="modal-header">
              <div className="modal-player-info">
                <div className="modal-player-avatar">📊</div>
                <div className="modal-player-details">
                  <h2>{selectedStat.name}</h2>
                  <p className="modal-player-meta">{selectedStat.importance} Importance • {(selectedStat.correlation * 100).toFixed(0)}% Correlation</p>
                </div>
              </div>
              <button className="modal-close" onClick={() => setShowStatModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <p style={{ marginBottom: '16px', color: 'var(--text-secondary)' }}>
                {selectedStat.explanation || `This statistic has shown strong correlation with success at this venue over the past 5 years of tournament data.`}
              </p>
              {selectedStat.yearlyCorrelations && (
                <div>
                  <h4 style={{ marginBottom: '12px', fontSize: '0.875rem', color: 'var(--text-tertiary)' }}>YEARLY BREAKDOWN</h4>
                  {Object.entries(selectedStat.yearlyCorrelations).map(([year, corr]) => (
                    <div key={year} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--border-light)' }}>
                      <span>{year}</span>
                      <span style={{ fontWeight: 600, color: 'var(--brand-primary)' }}>{(corr * 100).toFixed(0)}%</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Matchup Analysis Modal - TODO: Re-implement */}
    </div>
  );
}

// Helper function to format probability as American odds
function formatProbabilityAsOdds(probability) {
  if (!probability || probability <= 0 || probability >= 1) return '—';
  if (probability >= 0.5) {
    // Favorite: negative odds
    return Math.round(-100 * (probability / (1 - probability)));
  } else {
    // Underdog: positive odds
    return '+' + Math.round(100 * ((1 - probability) / probability));
  }
}

export default App;
